import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  ReactiveFormsModule,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { Router } from '@angular/router';
import { Observable, Subject, forkJoin, of } from 'rxjs';
import { finalize, map, take, takeUntil, tap } from 'rxjs';
import { MaterialUIModule } from '@app/shared/modules/material-ui/material-ui.module';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { DataGridComponent } from '@app/shared/custom-component/data-grid/data-grid.component';
import { DataGridVO } from '@app/shared/custom-component/data-grid/data-grid.model';
import { AlertService } from '@core/services/alert.service';
import { HttpService } from '@app/@core/services/http.service';
import { MyApplicationsService } from '@app/pages/my-application/my-app-service.service';
import { AppService } from '@app/@core/services/app.service';
import { LocalService } from '@app/@core/services/local.service';
import {
  AssignmentSessionData,
  errorMessages,
  ModalConfig,
  modalHeading,
  modalContent,
  modalButtonTitles
} from '@app/shared/constants/modal.model';
import { ModalComponent } from '@app/shared/custom-component/modal/modal.component';
import {
  StaffDocumentUploadNoticeContentVariant,
} from '@app/shared/constants/staff-document-upload-notice.util';
import { StaffDocumentUploadNoticeModalService } from '@app/shared/services/staff-document-upload-notice-modal.service';
import { getCancelFileUploadModalConfig } from '@app/shared/constants/cancel-file-upload-modal.util';
import { EducatorDuplicateDocumentGuardService } from '@app/shared/services/educator-duplicate-document-guard.service';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import * as FileSaver from 'file-saver';
import {
  APP_ID_ICON_GUIDANCE_FALLBACKS,
  BOOLEAN_VALUES,
  LOCK_STATUS,
  queues
} from '@app/shared/constants/queue.constants';
import {
  ApplicationOpenFromGridService,
  ApplicationOpenGridRow,
  LOOKUP_USER_ACTIVE_TAB_KEY,
  lookupUserReturnTargets,
  normalizeNumericApplicationId
} from '@app/shared/services/application-open-from-grid.service';
import { API_ENDPOINTS } from '@app/shared/constants/apiconstants';
import { CommonService } from '@app/shared/services/common.service';
import { EducatorDashboardDataRow } from '../../educator-dashboard-data.model';
import { EducatorDocumentPreviewModalService } from '../../educator-document-preview-modal.service';
import { resolveEducatorDocumentFileUrl } from '../../educator-document-download.util';
import { profileUploadDocumentsFromDashboardRow } from '../../educator-profile-documents.util';
import {
  ProfileDocumentLabelContext,
  PROFILE_DOC_EDUCATION_SECTION_LABELS,
  PROFILE_DOC_EXPERIENCE_SECTION_LABELS,
  isAcademicBackgroundInstitutionCategory,
  isNevadaSupportingSection,
  isOosSupportingSection,
  normalizeEducationProfileTabSection,
  resolveProfileDocumentCategoryLabel,
  resolveProfileDocumentTypeLabel,
  resolveProfileTabSectionFromLookupCategory,
  uniqueSystemACategoryKeysFromDocuments
} from '../../educator-profile-document-labels.util';
import {
  appendProfileUploadToDashboardRow,
  buildProfileUploadRowForLocalCache,
  toDuplicateLookupOptions
} from '../../educator-duplicate-document.util';
import { GridActionsComponent, GridAction, ColumnConfig } from '@app/shared/custom-component/grid-actions/grid-actions.component';
import {
  columnsForExcelExport,
  headersToColumnConfigs,
  mergeVisibleColumnDefaultsWithChooserLock,
  withColumnChooserLock
} from '../../lookup-grid-columns.util';
import { resolveLookupApplicationStatusDisplay } from '../../lookup-application-status.util';
import {
  educatorCommunicationUploadWithClientBlobPut,
  educatorDocumentUploadWithClientBlobPut
} from '../../educator-blob-upload.util';
import { LOOKUP_USER_SECTION_FORM_IDS } from '../../lookup-user-form-ids.constants';
import {
  canShowDeleteEducatorDocument,
  parseDocumentDeletionStaffHoursFromApi,
  resolveLoginRoleId,
  uploadTimeMsFromEducatorDocumentRow
} from '../../educator-document-delete-eligibility.util';
import { ROLE_ID } from '@app/shared/constants/rolesConstant.model';
import { ManageProfileService } from '@app/pages/manage-profile/manage-profile.service';
import { Tooltip, TooltipModule } from 'primeng/tooltip';
import { Dropdown, DropdownModule } from 'primeng/dropdown';
import { flipLookupUploadDropdownAbove } from '../../lookup-upload-dropdown.util';
import { SupportingDocumentRuleConfigService } from '@app/pages/page-designer/supporting-document-rule-config/supporting-document-rule-config.service';
import { SupportingDocumentGridOption } from '@app/pages/page-designer/supporting-document-grid/supporting-document-grid-config.model';


@Component({
  selector: 'lookup-user-activities-tab',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialUIModule,
    NgxMatSelectSearchModule,
    DataGridComponent,
    GridActionsComponent,
    TooltipModule,
    DropdownModule

  ],
  templateUrl: './lookup-user-activities-tab.component.html',
  styleUrls: ['./lookup-user-activities-tab.component.scss']
})
export class LookupUserActivitiesTabComponent implements OnChanges, OnDestroy, OnInit {
  @Input() personId: string | null = null;
  @Input() row: EducatorDashboardDataRow | null = null;
  @Output() requestDashboardRefresh = new EventEmitter<void>();

  showApplicationHistoryPanel = false;
  showNevadaPanel = false;
  showOosPanel = false;
  showCompliancePanel = false;
  showDocumentsPanel = false;

  documentsTab: 'active' | 'archived' = 'active';

  applicationHistoryGridConfig: DataGridVO = new DataGridVO();
  nevadaTestingGridConfig: DataGridVO = new DataGridVO();
  nevadaSupportingDocsGridConfig: DataGridVO = new DataGridVO();
  oosTestingGridConfig: DataGridVO = new DataGridVO();
  oosSupportingDocsGridConfig: DataGridVO = new DataGridVO();
  backgroundCheckGridConfig: DataGridVO = new DataGridVO();
  profileDocumentsGridConfig: DataGridVO = new DataGridVO();

  /** Empty grid message for all activity-section data grids. */
  private readonly activityGridNoRecordsText = 'No records';

  readonly activitiesExportColumnChooserActions: GridAction[] = [
    { type: 'export', id: 'activities_export', title: 'Export', tooltip: 'Export' },
    { type: 'columnChooser', id: 'activities_column_chooser', title: 'Column Chooser', tooltip: 'Column Chooser' }
  ];

  showAppHistoryColumnChooser = false;
  appHistoryAllColumns: ColumnConfig[] = [];
  appHistoryVisibleColumns: Record<string, boolean> = {};

  showNevadaTestingColumnChooser = false;
  nevadaTestingAllColumns: ColumnConfig[] = [];
  nevadaTestingVisibleColumns: Record<string, boolean> = {};

  showNvSupportingDocsColumnChooser = false;
  nvSupportingDocsAllColumns: ColumnConfig[] = [];
  nvSupportingDocsVisibleColumns: Record<string, boolean> = {};

  showOosTestingColumnChooser = false;
  oosTestingAllColumns: ColumnConfig[] = [];
  oosTestingVisibleColumns: Record<string, boolean> = {};

  showOosSupportingDocsColumnChooser = false;
  oosSupportingDocsAllColumns: ColumnConfig[] = [];
  oosSupportingDocsVisibleColumns: Record<string, boolean> = {};

  showBackgroundCheckColumnChooser = false;
  backgroundCheckAllColumns: ColumnConfig[] = [];
  backgroundCheckVisibleColumns: Record<string, boolean> = {};

  showProfileDocumentsColumnChooser = false;
  profileDocumentsAllColumns: ColumnConfig[] = [];
  profileDocumentsVisibleColumns: Record<string, boolean> = {};

  showComplianceSupportingDocsColumnChooser = false;
  complianceSupportingDocsAllColumns: ColumnConfig[] = [];
  complianceSupportingDocsVisibleColumns: Record<string, boolean> = {};
  complianceSupportingDocsGridConfig: DataGridVO = new DataGridVO();

  /**
   * Documents uploaded from Compliance → Supporting Documents: History Disclosure.
   * Dashboard SP returns `profile_tab_section` on `p_profile_upload` rows.
   */
  private static readonly COMPLIANCE_HISTORY_DISCLOSURE_PROFILE_SECTION = 'History Disclosure';

  /**
   * Picked from platform master lookup; `key` is `cd_document_category` on upload.
   */
  complianceHdCategoryRows: SupportingDocumentGridOption[] = [];

  complianceHdFilteredTypeOptions: SupportingDocumentGridOption[] = [];
  complianceHdTypesLoadError: string | null = null;
  /** When no usable History Disclosure category rows are resolved from lookup */
  complianceHdCategoryLoadError: string | null = null;
  complianceHdCategoryLookupMissing = false;
  @ViewChild('complianceHdUploadModalTpl') complianceHdUploadModalTpl!: TemplateRef<any>;
  private complianceHdUploadModalRef: NgbModalRef | null = null;
  complianceHdUploadForm!: FormGroup;
  private complianceSupportingUploadSaving = false;

  @ViewChild('complianceSupportingDocsGrid') complianceSupportingDocsGrid!: DataGridComponent;

  complianceDisclosures: Array<{
    key: string;
    question: string;
    yes: boolean | null;
    description: string;
  }> = [];

  showAddTestForm = false;
  addTestForm!: FormGroup;
  testNameOptions: { key: string; value: string }[] = [];
  /** Nevada Testing Add Test: filtered options for searchable mat-select (same pattern as OOS state). */
  filteredTestNameOptions: { key: string; value: string }[] = [];
  testNameFilterCtrl = new FormControl('');
  toastMessage: Record<string, string> = {};
  /** Spec M-keys (validation / error messages) from MESSAGES lookup */
  errorMessage: Record<string, string> = {};
  /** Spec P-keys (placeholder text) from PLACEHOLDER MESSAGES lookup */
  placeholderMessage: Record<string, string> = {};
  /** GENERIC MESSAGES lookup (G0029, G0030, G0032, …) for App ID icon tooltips */
  private guidanceMessages: Record<string, string> = {};
  todayDate: string = new Date().toISOString().split('T')[0];
  private lookupsLoaded = false;

  showOosAddTestForm = false;
  oosAddTestForm!: FormGroup;
  stateOptions: { key: string; value: string }[] = [];
  /** Out-of-State Testing: state options loaded from OUT OF STATE TEST lookup. */
  oosTestStateOptions: { key: string; value: string }[] = [];
  /** Out-of-State Testing: searchable state (same pattern as Experience Out-of-State Licensure). */
  filteredOosStateOptions: { key: string; value: string }[] = [];
  oosStateFilterCtrl = new FormControl('');
  oosExamNameOptions: { key: string; value: string }[] = [];
  oosResultOptions: { key: string; value: string }[] = [];
  examRequirements: any[] = [];


  @ViewChild('editDocumentModal') editDocumentModal!: TemplateRef<any>;
  editDocForm!: FormGroup;
  private editDocModalRef: NgbModalRef | null = null;
  private editDocRow: any = null;
  /** Values when Edit Document modal opened (Reset restores these). */
  private editDocFormSnapshot: Record<string, unknown> | null = null;
  /** Nevada/OOS supporting edit: lock category to the same lookup key as supporting upload (2 / 3). */
  private editDocSupportingSection: 'nevada' | 'oos' | null = null;
  /** Education/Experience tab section detected from profile_tab_section of the document being edited. */
  private editDocProfileTabSection: string | null = null;
  private static readonly EDUCATION_TAB_SECTIONS = ['academic-Background', 'Additional_prof_dev', 'employment_prof_dev', 'programs-upload'];
  private static readonly EXPERIENCE_TAB_SECTIONS = ['out-of-state-lic_notes', 'teaching-experience', 'employment-support', 'business-industry-support', 'verifiable-teaching-experience'];
  private static readonly ADDITIONAL_DEV_ALLOWED_TYPES = ['Professional Development Certificate', 'Proof of Completion', 'Transcripts'];
  private static readonly EMPLOYMENT_DEV_ALLOWED_TYPES = ['District / Charter Training Transcript', 'Professional Development Certificate'];
  docCategoryOptions: { id: number; key: string; value: string }[] = [];
  docTypeOptions: { id: number; parentid: number | null; key: string; value: string }[] = [];
  private allDocTypeOptions: { id: number; parentid: number | null; key: string; value: string }[] = [];
  private districtsLookup: { key: string; value: string }[] = [];
  private profDevProvidersLookup: { key: string; value: string }[] = [];
  filteredDocTypeOptions: { id: number; parentid: number | null; key: string; value: string }[] = [];

  /** Category options for the Profile Documents upload modal (loaded from platform master lookup). */
  uploadDocCategoryOptions: SupportingDocumentGridOption[] = [];

  /** Edit Document category dropdown options (concrete array for change detection). */
  editDocCategoryOptions: { id: number; key: string; value: string }[] = [];
  /** Lookup-backed edit: System A categories/types (all others except grid-string sections). */
  private editDocUsesSystemA = false;
  /** When set, load section-scoped System A categories (Personal, Compliance); else all top-level. */
  private editDocSystemASectionName: string | null = null;

  private buildEditDocCategoryOptions(): { id: number; key: string; value: string }[] {
    if (this.editDocProfileTabSection) {
      return this.buildEducationExpCategoryOptions(this.editDocProfileTabSection);
    }
    const section = this.editDocSupportingSection;
    if (section === 'nevada') {
      // Use System A options directly — same source as upload, numeric key guaranteed to match.
      return this.nvSupportingDocCategoryOptions as unknown as { id: number; key: string; value: string }[];
    }
    if (section === 'oos') {
      return this.oosSupportingDocCategoryOptions as unknown as { id: number; key: string; value: string }[];
    }
    return [];
  }

  /** Section-scoped System A lookup name for edit, or null → use all top-level categories. */
  private resolveSystemASectionNameForEdit(profileTabSection: string): string | null {
    const p = String(profileTabSection ?? '').trim();
    if (!p) return null;
    if (p === 'Name History') return 'Name History';
    if (p === 'Identity and Contact Information') return 'Identity and Contact Information';
    if (
      p === LookupUserActivitiesTabComponent.COMPLIANCE_HISTORY_DISCLOSURE_PROFILE_SECTION ||
      p.toLowerCase() === 'activities_compliance_history_disclosure'
    ) {
      return LookupUserActivitiesTabComponent.COMPLIANCE_HISTORY_DISCLOSURE_PROFILE_SECTION;
    }
    return null;
  }

  private loadEditDocSystemACategoryOptions(catKey: string, typeKey: string): void {
    const cats$ = this.editDocSystemASectionName
      ? this.supportingDocLookupService.getCategoriesBySectionName(this.editDocSystemASectionName)
      : this.supportingDocLookupService.getAllTopLevelCategories();

    cats$
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((cats: SupportingDocumentGridOption[]) => {
        this.editDocCategoryOptions = cats as any;
        if (!this.editDocSystemASectionName && !this.allSupportingDocCategories.length) {
          this.allSupportingDocCategories = cats;
        }
        let resolvedCatKey = catKey;
        let existingCat = cats.find(c => c.key === resolvedCatKey);
        if (!existingCat && this.editDocSystemASectionName && cats.length === 1) {
          resolvedCatKey = cats[0].key;
          existingCat = cats[0];
          this.editDocForm.patchValue({ category: resolvedCatKey });
        }
        if (existingCat) {
          this.supportingDocLookupService
            .getTypesByCategory(existingCat)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe((types: SupportingDocumentGridOption[]) => {
              this.filteredDocTypeOptions = types as any;
              this.supportingDocTypesCache[resolvedCatKey] = types;
            });
        } else if (typeKey) {
          this.filteredDocTypeOptions = [{ id: 0, parentid: null, key: typeKey, value: typeKey }];
        }
      });
  }

  @ViewChild('uploadDocumentModal') uploadDocumentModalTpl!: TemplateRef<any>;
  uploadDocForm!: FormGroup;
  private uploadDocModalRef: NgbModalRef | null = null;
  docUploadFile: File | null = null;
  docUploadError: string | null = null;
  docDropzoneActive = false;
  uploadFilteredDocTypeOptions: SupportingDocumentGridOption[] = [];

  @ViewChild('supportingDocUploadModalTpl') supportingDocUploadModalTpl!: TemplateRef<any>;
  supportingDocUploadSection: 'nevada' | 'oos' = 'nevada';
  supportingDocTypeOptions: { key: string; value: string }[] = [];
  nvSupportingDocCategoryOptions: SupportingDocumentGridOption[] = [];
  oosSupportingDocCategoryOptions: SupportingDocumentGridOption[] = [];
  /** Category options for the open Nevada/OOS supporting-doc upload modal. */
  supportingDocUploadCategoryOptions: SupportingDocumentGridOption[] = [];
  /** True when category is pre-selected and locked for Nevada/OOS supporting upload. */
  supportingDocUploadCategoryDisabled = false;
  /** Display label for locked category in Nevada/OOS supporting upload modal. */
  supportingDocUploadCategoryLabel = '';
  /** Category key pre-selected when modal opens (for discard-draft detection). */
  private initialSupportingDocUploadCategoryKey = '';
  supportingDocUploadForm!: FormGroup;
  private supportingDocUploadModalRef: NgbModalRef | null = null;
  /** Blocks cancel-confirm while Nevada/OOS supporting doc upload POST is in flight (Personal tab parity). */
  supportingDocUploadSaving = false;
  /** Blocks cancel-confirm while Profile Documents upload POST is in flight. */
  private profileDocumentsUploadSaving = false;

  @ViewChild('appHistoryGrid') appHistoryGrid!: DataGridComponent;
  @ViewChild('nevadaTestGrid') nevadaTestGrid!: DataGridComponent;
  @ViewChild('oosTestGrid') oosTestGrid!: DataGridComponent;
  @ViewChild('backgroundCheckGrid') backgroundCheckGrid!: DataGridComponent;
  @ViewChild('profileDocumentsGrid') profileDocumentsGrid!: DataGridComponent;
  @ViewChild('nvSupportingDocsGrid') nvSupportingDocsGrid!: DataGridComponent;
  @ViewChild('oosSupportingDocsGrid') oosSupportingDocsGrid!: DataGridComponent;

  @ViewChild('editNevadaTestModal') editNevadaTestModal!: TemplateRef<any>;
  editNevadaTestForm!: FormGroup;
  private editNevadaTestRow: any = null;
  editNevadaTestModalRef: NgbModalRef | null = null;
  private editNevadaTestFormSnapshot: Record<string, unknown> | null = null;

  @ViewChild('editOosTestModal') editOosTestModal!: TemplateRef<any>;
  editOosTestForm!: FormGroup;
  private editOosTestRow: any = null;
  editOosTestModalRef: NgbModalRef | null = null;
  private editOosTestFormSnapshot: Record<string, unknown> | null = null;

  private _lastApplicationHistoryRows: any[] = [];
  private readonly destroy$ = new Subject<void>();

  /** Metadata-driven headers loaded from JSON (same pattern as queues / application-dashboard). */
  private activitiesMetadataHeaders: Record<string, any[]> = {};
  private metadataHeadersLoaded = false;
  private static readonly ACTIVITIES_HEADERS_JSON = '../assets/metadata/lookup-user-activities-headers.json';

  /** BR0036: hours staff may delete after upload (`Documents` / `document_deletion_time_left`). */
  documentDeletionStaffHours: number | null = null;
  private loginRoleIdForDocumentDelete: number | null = null;

  /** True only for roles that may view the Compliance section and Case ID column (457, 451, 450). */
  canViewCompliance = false;

  /** MM/DD/YYYY; empty defers to `required` (M0066). Invalid format or future date → `testDateFormat` (M0063). */
  private activitiesTestDateValidator(control: AbstractControl): ValidationErrors | null {
    const v = String(control.value ?? '').trim();
    if (v === '') {
      return null;
    }
    const m = v.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!m) {
      return { testDateFormat: true };
    }
    const month = parseInt(m[1], 10);
    const day = parseInt(m[2], 10);
    const year = parseInt(m[3], 10);
    if (month < 1 || month > 12 || day < 1 || day > 31) {
      return { testDateFormat: true };
    }
    const d = new Date(year, month - 1, day);
    if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) {
      return { testDateFormat: true };
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (d.getTime() > today.getTime()) {
      return { testDateFormat: true };
    }
    return null;
  }
  /** All System A supporting-doc categories (cached for synchronous grid label resolution). */
  private allSupportingDocCategories: SupportingDocumentGridOption[] = [];
  /** System A type options keyed by category key (populated as types are loaded). */
  private supportingDocTypesCache: Record<string, SupportingDocumentGridOption[]> = {};

  /** Score field: allow only valid prefixes of `[0-9]{1,4}(\.[0-9]{1,2})?` (max 9999.99). */
  private isValidScorePrefix(s: string): boolean {
    if (s === '') {
      return true;
    }
    return /^([0-9]{1,4})(\.([0-9]{0,2})?)?$/.test(s);
  }

  /** Block keystrokes that would push score past 9999.99 (4 int digits + 2 dec digits). */
  onScoreKeydown(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }
    const navKeys = new Set([
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End'
    ]);
    if (navKeys.has(event.key)) {
      return;
    }
    if (event.key.length !== 1) {
      return;
    }
    const input = event.target as HTMLInputElement;
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? input.value.length;
    const next = input.value.slice(0, start) + event.key + input.value.slice(end);
    if (!this.isValidScorePrefix(next)) {
      event.preventDefault();
    }
  }

  /** Paste guard: reject clipboard content that would create an invalid score string. */
  onScorePaste(event: ClipboardEvent): void {
    const input = event.target as HTMLInputElement;
    const pasted = event.clipboardData?.getData('text') ?? '';
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? input.value.length;
    const next = input.value.slice(0, start) + pasted + input.value.slice(end);
    if (!this.isValidScorePrefix(next)) {
      event.preventDefault();
    }
  }

  constructor(
    private readonly alertService: AlertService,
    private readonly httpService: HttpService,
    private readonly fb: FormBuilder,
    private readonly applicationOpenFromGrid: ApplicationOpenFromGridService,
    private readonly myAppService: MyApplicationsService,
    private readonly appService: AppService,
    private readonly sessionStorage: LocalService,
    private readonly router: Router,
    private readonly ngbService: NgbModal,
    private readonly staffDocNoticeModal: StaffDocumentUploadNoticeModalService,
    private readonly duplicateDocGuard: EducatorDuplicateDocumentGuardService,
    private readonly commonService: CommonService,
    private readonly manageProfileService: ManageProfileService,
    private readonly documentPreviewModal: EducatorDocumentPreviewModalService,
    private readonly supportingDocLookupService: SupportingDocumentRuleConfigService
  ) {
    this.addTestForm = this.fb.group({
      testName: ['', Validators.required],
      testDate: ['', [Validators.required, (c: AbstractControl) => this.activitiesTestDateValidator(c)]],
      score: [
        null,
        [
          Validators.required,
          Validators.min(0.01),
          Validators.max(9999.99),
          Validators.pattern(/^(0\.\d{1,2}|[1-9]\d{0,3}(\.\d{1,2})?)$/)
        ]
      ]
    });
    this.oosAddTestForm = this.fb.group({
      state: ['', Validators.required],
      testName: [
        '',
        [
          Validators.required,
          Validators.maxLength(60),
          Validators.pattern(/^(?=.*\S)[A-Za-z0-9 .'\-]+$/)
        ]
      ],
      testDate: ['', [Validators.required, (c: AbstractControl) => this.activitiesTestDateValidator(c)]],
      score: [
        null,
        [
          Validators.required,
          Validators.min(0.01),
          Validators.max(9999.99),
          Validators.pattern(/^(0\.\d{1,2}|[1-9]\d{0,3}(\.\d{1,2})?)$/)
        ]
      ],
      result: ['', Validators.required]
    });
    this.editDocForm = this.fb.group({
      category: ['', Validators.required],
      type: ['', Validators.required],
      notes: ['', Validators.required]
    });
    this.uploadDocForm = this.fb.group({
      category: ['', Validators.required],
      type: ['', Validators.required],
      notes: ['']
    });
    this.supportingDocUploadForm = this.fb.group({
      category: ['', Validators.required],
      type: ['', Validators.required],
      notes: ['']
    });
    this.complianceHdUploadForm = this.fb.group({
      category: ['', Validators.required],
      type: ['', Validators.required]
    });
    this.editNevadaTestForm = this.fb.group({
      testName: ['', Validators.required],
      testDate: ['', [Validators.required, (c: AbstractControl) => this.activitiesTestDateValidator(c)]],
      score: [null, [Validators.required, Validators.min(0), Validators.max(9999.99), Validators.pattern(/^(0\.\d{1,2}|[1-9]\d{0,3}(\.\d{1,2})?)$/)]]
    });
    this.editOosTestForm = this.fb.group({
      state: ['', Validators.required],
      testName: [
        '',
        [
          Validators.required,
          Validators.maxLength(60),
          Validators.pattern(/^(?=.*\S)[A-Za-z0-9 .'\-]+$/)
        ]
      ],
      testDate: ['', [Validators.required, (c: AbstractControl) => this.activitiesTestDateValidator(c)]],
      score: [
        null,
        [
          Validators.required,
          Validators.min(0.01),
          Validators.max(9999.99),
          Validators.pattern(/^(0\.\d{1,2}|[1-9]\d{0,3}(\.\d{1,2})?)$/)
        ]
      ],
      result: ['', Validators.required]
    });
    this.oosStateFilterCtrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.filterOosStateOptions());
    this.testNameFilterCtrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.filterTestNameOptions());
  }

  ngOnInit(): void {
    this.loginRoleIdForDocumentDelete = resolveLoginRoleId(this.sessionStorage, this.commonService);
    const COMPLIANCE_ROLES = [ROLE_ID.NDE_Administrator, ROLE_ID.Compliance_Investigator, ROLE_ID.Chief_Compliance_Investigator];
    this.canViewCompliance = COMPLIANCE_ROLES.includes(this.loginRoleIdForDocumentDelete as number);

    this.manageProfileService
      .getBusinessRuleByKey('Documents', 'document_deletion_time_left')
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (res: unknown) => {
          this.documentDeletionStaffHours = parseDocumentDeletionStaffHoursFromApi(res);
        },
        error: () => {
          this.documentDeletionStaffHours = null;
        }
      });
  }

  /** BR0036: hide delete outside staff window unless NDE Administrator. */
  canShowDeleteProfileDocument(row: any): boolean {
    const roleId =
      this.loginRoleIdForDocumentDelete ?? resolveLoginRoleId(this.sessionStorage, this.commonService);
    return canShowDeleteEducatorDocument(row, this.documentDeletionStaffHours, roleId);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private filterOosStateOptions(): void {
    const search = this.oosStateFilterCtrl.value?.toLowerCase() || '';
    if (!search) {
      this.filteredOosStateOptions = [...this.oosTestStateOptions];
    } else {
      this.filteredOosStateOptions = this.oosTestStateOptions.filter(s => s.value.toLowerCase().includes(search));
    }
  }

  onOosStateSelectOpened(opened: boolean): void {
    if (opened) {
      this.oosStateFilterCtrl.setValue('');
      this.filteredOosStateOptions = [...this.oosTestStateOptions];
      setTimeout(() => {
        const panel = document.querySelector('.activities-oos-state-search-panel');
        if (panel) {
          (panel as HTMLElement).scrollTop = 0;
        }
      });
    }
  }

  private filterTestNameOptions(): void {
    const search = this.testNameFilterCtrl.value?.toLowerCase() || '';
    this.filteredTestNameOptions = !search
      ? [...this.testNameOptions]
      : this.testNameOptions.filter(o => o.value.toLowerCase().includes(search));
  }

  private resolveNevadaTestResult(testNameKey: string, score: number): string {
    const option = this.testNameOptions.find(o => o.key === testNameKey);
    if (!option?.value) return '';
    return this.resolveResultFromExamRequirements(option.value, score);
  }

  onNvTestNameSelectOpened(opened: boolean): void {
    if (opened) {
      this.testNameFilterCtrl.setValue('');
      this.filteredTestNameOptions = [...this.testNameOptions];
      setTimeout(() => {
        const panel = document.querySelector('.activities-nv-test-name-panel');
        if (panel) {
          (panel as HTMLElement).scrollTop = 0;
        }
      });
    }
  }

  readonly onLookupApplicationIdClick = (applicationInternalId: string, event?: Event): void => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    const idStr =
      applicationInternalId != null && String(applicationInternalId).trim() !== ''
        ? String(applicationInternalId).trim()
        : '';
    if (!idStr) {
      this.alertService.warn('No application is linked to this row.');
      return;
    }
    const gridRow = (this._lastApplicationHistoryRows || []).find(r => String(r.id ?? '').trim() === idStr);
    const selectedApp = this.buildSelectedAppForOpenFromLookup(idStr, gridRow);

    const currentUserName = this.getCurrentUserFullName();
    const lockStatus = String(gridRow?.lockstatus || '').toUpperCase();
    const lockedBy = String(gridRow?.locked_by || '').trim();
    const isLockedByOther = lockedBy !== '' &&
      lockedBy.replace(/\s+/g, '') !== currentUserName.replace(/\s+/g, '');

    if (lockStatus === LOCK_STATUS[3].LONG_NM.toUpperCase() && isLockedByOther) {
      this.showLockedModal(lockedBy, selectedApp);
      return;
    }
    if (lockStatus === LOCK_STATUS[0].CD && isLockedByOther) {
      this.showTempLockedModal(lockedBy, selectedApp);
      return;
    }

    this.applicationOpenFromGrid.openApplicationLikeDataGrid(selectedApp, {
      returnTargets: lookupUserReturnTargets(this.personId),
      sourceTab: 'activities',
      sourcePersonId: this.personId ?? undefined
    });
  };

  readonly onLookupCaseIdClick = (applicationInternalId: string, event?: Event): void => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    const idStr = applicationInternalId != null ? String(applicationInternalId).trim() : '';
    if (!idStr) {
      this.alertService.warn('No application is linked to this case.');
      return;
    }
    const selectedApp = (this._lastApplicationHistoryRows || []).find(
      r => String(r.id ?? '').trim() === idStr || String(r.application_id ?? '').trim() === idStr
    );
    if (!selectedApp) {
      this.alertService.warn('Could not find the application for this case.');
      return;
    }

    this.myAppService
      .getApplication(selectedApp.id)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: personResponse => {
          const personData = personResponse.data[0];
          this.sessionStorage.setItem('personData', JSON.stringify(personData));

          const educatorName = selectedApp.educator_name || '';
          const bannerheader: Record<string, string> = {
            'Case ID': selectedApp.case_id,
            'Case Type': 'Application',
            'Educator Name': educatorName,
          };
          this.sessionStorage.setItem('navigationData', JSON.stringify({ bannerheader }));

          this.myAppService
            .getAppData(`appId=${selectedApp.id}`)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
              next: appResponse => {
                const { caseversionid } = appResponse.data.data[0];
                if (caseversionid) {
                  const returnTargets =
                    lookupUserReturnTargets(this.personId) ?? {
                      backToHome: { name: '/pages/lookup-search' },
                      backToGridPage: { name: '/lookup-search' },
                    };
                  this.sessionStorage.setItem('parentGridPage', '');
                  this.sessionStorage.setItem('backToHome', JSON.stringify(returnTargets.backToHome));
                  this.sessionStorage.setItem('backToGridPage', JSON.stringify(returnTargets.backToGridPage));
                  // Normalize to a number so JSON.stringify produces "5451" not '"5451"'.
                  // DynamicRibbonComponent reads youthID from session without JSON.parse,
                  // so a string id stored via JSON.stringify gains an extra layer of quotes.
                  const idForSession = normalizeNumericApplicationId(selectedApp.id) ?? selectedApp.id;
                  const sessionData: AssignmentSessionData = {
                    youthID: idForSession,
                    youthNumber: selectedApp.application_number ?? selectedApp.application_id ?? selectedApp.id,
                    applicationID: idForSession,
                    isFirstSave: 'false',
                    applicationType: '',
                    currApplicationType: '',
                    backToHome: returnTargets.backToHome,
                    backToGridPage: returnTargets.backToGridPage,
                  };
                  Object.entries(sessionData).forEach(([key, value]) => {
                    if (key === 'youthNumber' && typeof value === 'string') {
                      this.sessionStorage.setItem(key, value);
                    } else {
                      this.sessionStorage.setItem(key, JSON.stringify(value));
                    }
                  });
                  this.sessionStorage.setItem('backButtonText', 'Close');
                  if (this.personId) {
                    this.sessionStorage.setItem(
                      LOOKUP_USER_ACTIVE_TAB_KEY,
                      JSON.stringify({ personId: String(this.personId), tab: 'activities' })
                    );
                  }
                  this.router.navigate([`/pages/dynamic-routing/tab/${caseversionid}`]);
                  this.appService.addValue('currentPage', 'Case');
                } else {
                  console.error(errorMessages.noActiveVersionId);
                  this.alertService.warn('No active case version found for this application.');
                }
              },
              error: err => {
                console.error('Error getting app data for case navigation:', err);
                this.alertService.warn('Failed to load case data.');
              }
            });
        },
        error: err => {
          console.error('Error getting person data for case navigation:', err);
          this.alertService.warn('Failed to load person data for case navigation.');
        }
      });
  };

  private getCurrentUserFullName(): string {
    const raw = this.sessionStorage.getItem('user');
    const user = typeof raw === 'string' && raw.trim() !== '' ? JSON.parse(raw) : raw || {};
    return user?.firstname && user?.lastname
      ? `${user.firstname} ${user.lastname}`
      : '';
  }

  private showLockedModal(lockedByName: string, selectedApp: ApplicationOpenGridRow): void {
    const options: NgbModalOptions = { backdrop: 'static', centered: true, size: 'md' };
    const modalRef = this.ngbService.open(ModalComponent, options);
    const config: ModalConfig = {
      buttons: [
        { text: modalButtonTitles.cancel, code: BOOLEAN_VALUES[1].CD, btnOutline: true },
        { text: modalButtonTitles.viewApplication, code: BOOLEAN_VALUES[0].CD, btnOutline: false }
      ],
      heading: modalHeading.viewLockedAp,
      modalContent: modalContent.viewLockedApp(lockedByName),
      hasTemplate: true
    };
    modalRef.componentInstance.modalConfig = config;
    modalRef.componentInstance.emitData.pipe(take(1)).subscribe((response: any) => {
      this.ngbService.dismissAll();
      if (response?.confirmed === true) {
        this.sessionStorage.setItem('applicationmode', JSON.stringify('view'));
        this.applicationOpenFromGrid.openApplicationLikeDataGrid(selectedApp, {
          isViewMode: true,
          returnTargets: lookupUserReturnTargets(this.personId),
          sourceTab: 'activities',
          sourcePersonId: this.personId ?? undefined
        });
      }
    });
  }

  private showTempLockedModal(lockedByName: string, selectedApp: ApplicationOpenGridRow): void {
    const options: NgbModalOptions = { backdrop: 'static', centered: true, size: 'md' };
    const modalRef = this.ngbService.open(ModalComponent, options);
    const config: ModalConfig = {
      buttons: [
        { text: modalButtonTitles.cancel, code: BOOLEAN_VALUES[1].CD, btnOutline: true },
        { text: modalButtonTitles.viewApplication, code: BOOLEAN_VALUES[0].CD, btnOutline: false }
      ],
      heading: modalHeading.AssignApplication,
      modalContent: modalContent.tempLockApp(lockedByName),
      hasTemplate: true
    };
    modalRef.componentInstance.modalConfig = config;
    modalRef.componentInstance.emitData.pipe(take(1)).subscribe((response: any) => {
      this.ngbService.dismissAll();
      if (response?.confirmed === true) {
        this.sessionStorage.setItem('applicationmode', JSON.stringify('view'));
        this.applicationOpenFromGrid.openApplicationLikeDataGrid(selectedApp, {
          isViewMode: true,
          returnTargets: lookupUserReturnTargets(this.personId),
          sourceTab: 'activities',
          sourcePersonId: this.personId ?? undefined
        });
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['row'] || changes['personId']) {
      this.rebuildGridsAndCompliance();
      if (!this.lookupsLoaded) {
        this.loadLookups();
      }
    }
  }

  stubAction(feature: string): void {
    console.log(`[LookupUserActivitiesTab] stub: ${feature}`);
    this.alertService.info(`${feature} — coming soon.`);
  }

  private getActivitiesTestDateForm(kind: 'add' | 'oos' | 'editNv' | 'editOos'): FormGroup {
    switch (kind) {
      case 'add':
        return this.addTestForm;
      case 'oos':
        return this.oosAddTestForm;
      case 'editNv':
        return this.editNevadaTestForm;
      case 'editOos':
        return this.editOosTestForm;
      default:
        return this.addTestForm;
    }
  }

  private getActivitiesTestDateInputId(kind: 'add' | 'oos' | 'editNv' | 'editOos'): string {
    switch (kind) {
      case 'add':
        return 'activities_nv_test_date';
      case 'oos':
        return 'activities_oos_test_date';
      case 'editNv':
        return 'activities_edit_nevada_test_date';
      case 'editOos':
        return 'activities_edit_oos_test_date';
      default:
        return 'activities_nv_test_date';
    }
  }

  /** MM/DD/YYYY keyboard entry (parity with Personal tab DOB). */
  onActivitiesTestDateInput(event: Event, kind: 'add' | 'oos' | 'editNv' | 'editOos'): void {
    const form = this.getActivitiesTestDateForm(kind);
    const target = event.target as HTMLInputElement;
    const value = (target?.value || '').replace(/\D/g, '');
    const limited = value.length > 8 ? value.substring(0, 8) : value;
    let formatted = limited;
    if (limited.length >= 5) {
      formatted = `${limited.substring(0, 2)}/${limited.substring(2, 4)}/${limited.substring(4, 8)}`;
    } else if (limited.length >= 3) {
      formatted = `${limited.substring(0, 2)}/${limited.substring(2, 4)}`;
    } else if (limited.length >= 1) {
      formatted = limited.substring(0, 2);
    }
    form.get('testDate')?.setValue(formatted, { emitEvent: false });
  }

  /** Material calendar_month icon + hidden date input (same pattern as Personal tab DOB). */
  openActivitiesTestDatePicker(event: Event, kind: 'add' | 'oos' | 'editNv' | 'editOos'): void {
    event.stopPropagation();
    const inputId = this.getActivitiesTestDateInputId(kind);
    const form = this.getActivitiesTestDateForm(kind);
    const anchor = document.getElementById(inputId);
    if (!anchor) {
      return;
    }
    const rect = anchor.getBoundingClientRect();
    const dateInput = document.createElement('input') as HTMLInputElement;
    dateInput.type = 'date';
    dateInput.style.position = 'absolute';
    dateInput.style.top = rect.top + window.scrollY + 5 + 'px';
    dateInput.style.left = rect.left + 'px';
    dateInput.style.width = '120px';
    dateInput.style.height = '35px';
    dateInput.style.opacity = '0.01';
    dateInput.style.border = 'none';
    dateInput.style.zIndex = '9999';
    dateInput.style.pointerEvents = 'auto';
    dateInput.max = this.todayDate;
    const currentValue = form.get('testDate')?.value as string;
    if (currentValue && currentValue.includes('/')) {
      const parts = currentValue.split('/');
      if (parts.length === 3 && parts[2].length === 4) {
        const month = parts[0].padStart(2, '0');
        const day = parts[1].padStart(2, '0');
        const year = parts[2];
        dateInput.value = `${year}-${month}-${day}`;
      }
    } else if (currentValue && /^\d{4}-\d{2}-\d{2}/.test(String(currentValue))) {
      dateInput.value = String(currentValue).substring(0, 10);
    }
    document.body.appendChild(dateInput);
    const cleanup = () => {
      if (document.body.contains(dateInput)) {
        dateInput.removeEventListener('change', changeHandler);
        dateInput.removeEventListener('blur', blurHandler);
        document.body.removeChild(dateInput);
      }
    };
    const changeHandler = (changeEvent: Event) => {
      const target = (changeEvent.target as HTMLInputElement);
      const selectedDate = target?.value;
      if (selectedDate) {
        const dateParts = selectedDate.split('-');
        if (dateParts.length === 3) {
          const formattedDate = `${dateParts[1]}/${dateParts[2]}/${dateParts[0]}`;
          form.get('testDate')?.setValue(formattedDate);
          form.get('testDate')?.markAsTouched();
          form.get('testDate')?.updateValueAndValidity();
        }
      }
      cleanup();
    };
    const blurHandler = () => setTimeout(cleanup, 100);
    dateInput.addEventListener('change', changeHandler);
    dateInput.addEventListener('blur', blurHandler);
    dateInput.focus();
    const inputEl = dateInput as HTMLInputElement;
    if (typeof (inputEl as any).showPicker === 'function') {
      try {
        (inputEl as any).showPicker();
      } catch {
        inputEl.click();
      }
    } else {
      inputEl.click();
    }
  }

  private testDateDisplayToIso(s: string | null | undefined): string {
    if (s == null || s === '') {
      return '';
    }
    const t = String(s).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(t)) {
      return t.substring(0, 10);
    }
    const m = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (m) {
      const mo = m[1].padStart(2, '0');
      const d = m[2].padStart(2, '0');
      const y = m[3];
      return `${y}-${mo}-${d}`;
    }
    return t;
  }

  private testDateApiToDisplay(raw: unknown): string {
    if (raw == null) {
      return '';
    }
    const s = String(raw).trim();
    if (s.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(s)) {
      const y = s.substring(0, 4);
      const mo = s.substring(5, 7);
      const d = s.substring(8, 10);
      return `${mo}/${d}/${y}`;
    }
    if (s.includes('/')) {
      return s;
    }
    return s;
  }

  /**
   * FormGroup.reset() with no args sets values to null; native select placeholder options use
   * value="" which only matches '', so the closed control would stay blank without explicit defaults.
   */
  private resetAddTestForm(): void {
    this.addTestForm.reset({
      testName: '',
      testDate: '',
      score: null
    });
    this.testNameFilterCtrl.setValue('');
    this.filteredTestNameOptions = [...this.testNameOptions];
  }

  private resetOosAddTestForm(): void {
    this.oosAddTestForm.reset({
      state: '',
      testName: '',
      testDate: '',
      score: null,
      result: ''
    });
  }

  onAddTest(): void {
    this.resetAddTestForm();
    this.showAddTestForm = true;
  }

  onCancelAddTest(): void {
    this.showAddTestForm = false;
    this.resetAddTestForm();
  }

  onSaveAddTest(): void {
    if (this.addTestForm.invalid) {
      this.addTestForm.markAllAsTouched();
      this.alertService.error(this.toastMessage['T0017'] || 'Please fill in all required fields.');
      return;
    }
    if (this.isNevadaTestDuplicate(this.addTestForm)) {
      this.alertService.error('Duplicate entry detected. This record already exists.');
      return;
    }
    const f = this.addTestForm.value;
    const result = this.resolveNevadaTestResult(f.testName, Number(f.score));
    const payload = {
      personId: Number(this.personId),
      cdTestName: f.testName,
      testDate: this.testDateDisplayToIso(f.testDate),
      score: Number(f.score),
      result,
      createdBy: this.getCurrentUserId()
    };
    this.httpService.post(API_ENDPOINTS.EDUCATOR_ADD_NEVADA_TEST, payload)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          const success = res?.status === 'success' || res?.data?.success;
          if (success) {
            this.alertService.success(this.toastMessage['T0005'] || 'Saved successfully.');
            this.showAddTestForm = false;
            this.resetAddTestForm();
            this.requestDashboardRefresh.emit();
          } else {
            this.alertService.error('Failed to save Nevada test.');
          }
        },
        error: (err: any) => {
          console.error('Error saving Nevada test:', err);
          this.alertService.error('Failed to save Nevada test.');
        }
      });
  }

  onAddOosTest(): void {
    this.resetOosAddTestForm();
    this.showOosAddTestForm = true;
  }

  onCancelOosAddTest(): void {
    this.showOosAddTestForm = false;
    this.resetOosAddTestForm();
  }

  onSaveOosAddTest(): void {
    if (this.oosAddTestForm.invalid) {
      this.oosAddTestForm.markAllAsTouched();
      this.alertService.error(this.toastMessage['T0017'] || 'Please fill in all required fields.');
      return;
    }
    if (this.isOosTestDuplicate(this.oosAddTestForm)) {
      this.alertService.error('Duplicate entry detected. This record already exists.');
      return;
    }
    const f = this.oosAddTestForm.value;
    const payload = {
      personId: Number(this.personId),
      cdState: f.state,
      testName: f.testName,
      testDate: this.testDateDisplayToIso(f.testDate),
      score: Number(f.score),
      cdResult: f.result,
      createdBy: this.getCurrentUserId()
    };
    this.httpService.post(API_ENDPOINTS.EDUCATOR_ADD_OOS_TEST, payload)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          const success = res?.status === 'success' || res?.data?.success;
          if (success) {
            this.alertService.success(this.toastMessage['T0005'] || 'Saved successfully.');
            this.showOosAddTestForm = false;
            this.resetOosAddTestForm();
            this.requestDashboardRefresh.emit();
          } else {
            this.alertService.error('Failed to save out-of-state test.');
          }
        },
        error: (err: any) => {
          console.error('Error saving out-of-state test:', err);
          this.alertService.error('Failed to save out-of-state test.');
        }
      });
  }

  private loadLookups(): void {
    this.lookupsLoaded = true;
    forkJoin([
      this.httpService.get(API_ENDPOINTS.LOOKUP_TEST_NAMES),
      this.httpService.get(API_ENDPOINTS.TOAST_MESSAGES),
      this.httpService.get(API_ENDPOINTS.MESSAGES),
      this.httpService.get(API_ENDPOINTS.PLACEHOLDER_MESSAGES),
      this.httpService.get(API_ENDPOINTS.LOOKUP_STATES),
      this.httpService.get(API_ENDPOINTS.LOOKUP_OOS_EXAM_NAMES),
      this.httpService.get(API_ENDPOINTS.LOOKUP_OOS_RESULT),
      this.httpService.get(API_ENDPOINTS.LOOKUP_DOCUMENT_CATEGORY),
      this.httpService.get(API_ENDPOINTS.LOOKUP_DOCUMENT_TYPE),
      this.httpService.get(API_ENDPOINTS.GENERIC_MESSAGES),
      this.httpService.get(`${API_ENDPOINTS.DOMAIN_LOOKUP_BY_TYPE}OUT OF STATE TEST`),
      this.httpService.get(API_ENDPOINTS.BUSINESS_RULES),
      this.httpService.get(`${API_ENDPOINTS.DOMAIN_LOOKUP_BY_TYPE}DISTRICT`),
      this.httpService.get(`${API_ENDPOINTS.DOMAIN_LOOKUP_BY_TYPE}PROF-DEV-PROVIDER`)
    ]).pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (results: any[]) => {
          const mapOptions = (data: any[]) => (data || []).map((item: any) => ({
            key: item.key ?? item.cd ?? item.lookupid?.toString() ?? '',
            value: item.value ?? item.name ?? item.description ?? ''
          }));

          this.testNameOptions = mapOptions(results[0]?.data);
          this.filteredTestNameOptions = [...this.testNameOptions];

          this.toastMessage = {};
          (results[1]?.data || []).forEach((msg: { key?: string; value?: string }) => {
            if (msg.key != null && msg.value !== undefined) this.toastMessage[msg.key] = msg.value;
          });

          this.errorMessage = {};
          (results[2]?.data || []).forEach((msg: { key?: string; value?: string }) => {
            if (msg.key != null && msg.value !== undefined) this.errorMessage[msg.key] = msg.value;
          });

          this.placeholderMessage = {};
          (results[3]?.data || []).forEach((p: { key?: string; value?: string }) => {
            if (p.key != null && p.value !== undefined) this.placeholderMessage[p.key] = p.value;
          });

          this.stateOptions = mapOptions(results[4]?.data)
            .filter((s: { key: string; value: string }) => s.value.toUpperCase() !== 'NV' && s.value.toUpperCase() !== 'NEVADA');
          this.oosTestStateOptions = mapOptions(results[10]?.data)
            .filter((s: { key: string; value: string }) => s.key !== undefined && s.value !== undefined)
            .sort((a: { key: string; value: string }, b: { key: string; value: string }) => a.value.localeCompare(b.value));
          this.filteredOosStateOptions = [...this.oosTestStateOptions];
          this.oosExamNameOptions = mapOptions(results[5]?.data);
          this.oosResultOptions = mapOptions(results[6]?.data);

          const mapLookupFull = (data: any[]) => (data || []).map((item: any) => ({
            id: item.id ?? 0,
            parentid: item.parentid ?? null,
            key: String(item.key ?? item.cd ?? item.lookupid ?? ''),
            value: String(item.value ?? item.name ?? item.description ?? '')
          })).filter(d => d.key && d.value);

          this.docCategoryOptions = mapLookupFull(results[7]?.data ?? results[7]);
          this.allDocTypeOptions = mapLookupFull(results[8]?.data ?? results[8]);
          this.filteredDocTypeOptions = [];
          this.districtsLookup = mapOptions(results[12]?.data ?? []);
          this.profDevProvidersLookup = mapOptions(results[13]?.data ?? []);

          this.guidanceMessages = {};
          (results[9]?.data || []).forEach((msg: { key?: string; value?: string }) => {
            if (msg.key != null && msg.value !== undefined) {
              this.guidanceMessages[msg.key] = msg.value;
            }
          });

          this.examRequirements = results[11]?.data?.data?.recordsExamRequirements ?? [];

          // Resolve supporting-document categories, then rebuild grids with System A label decode.
          forkJoin({
            nv: this.supportingDocLookupService.getCategoriesBySectionName('Nevada Praxis Testing'),
            oos: this.supportingDocLookupService.getCategoriesBySectionName('Other Testing'),
            all: this.supportingDocLookupService.getAllTopLevelCategories()
          })
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe(({ nv, oos, all }) => {
              this.nvSupportingDocCategoryOptions = nv;
              this.nvSupportingDocCategory = nv[0] ?? null;
              this.oosSupportingDocCategoryOptions = oos;
              this.oosSupportingDocCategory = oos[0] ?? null;
              this.allSupportingDocCategories = all;
              this.rebuildGridsAndCompliance();
            });
        },
        error: (err: any) => {
          console.error('Error loading lookups for Activities tab:', err);
        }
      });
  }

  onDocumentsTab(tab: 'active' | 'archived'): void {
    if (tab === this.documentsTab) return;
    this.documentsTab = tab;
    this.preloadSupportingDocTypesForDocuments()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(() => this.rebuildProfileDocumentsGrid());
  }

  toggleApplicationHistoryPanel(): void {
    this.showApplicationHistoryPanel = !this.showApplicationHistoryPanel;
  }
  toggleNevadaPanel(): void {
    this.showNevadaPanel = !this.showNevadaPanel;
  }
  toggleOosPanel(): void {
    this.showOosPanel = !this.showOosPanel;
  }
  toggleCompliancePanel(): void {
    this.showCompliancePanel = !this.showCompliancePanel;
  }
  toggleDocumentsPanel(): void {
    this.showDocumentsPanel = !this.showDocumentsPanel;
  }

  private profileName(): string {
    const prof = this.row?.p_profile;
    if (prof && typeof prof === 'object' && !Array.isArray(prof)) {
      const n = (prof as { person_name?: string }).person_name;
      if (n && String(n).trim()) return String(n).trim();
    }
    if (Array.isArray(prof) && prof[0]?.person_name) return String(prof[0].person_name).trim();
    return '';
  }

  private licenseNumberForOpenFromLookup(internalId: string, gridRow?: any): string | undefined {
    const fromGrid = gridRow?.license_number ?? gridRow?.licenseNumber;
    if (fromGrid != null) {
      const g = String(fromGrid).trim();
      if (g && g !== '---') {
        return g;
      }
    }
    const dtl = this.row?.p_license_dtls;
    if (!Array.isArray(dtl) || dtl.length === 0) {
      return undefined;
    }
    const idNum = Number(internalId);
    const byApp = dtl.find(
      (l: any) =>
        l?.license_number != null &&
        String(l.license_number).trim() !== '' &&
        l.application_id != null &&
        Number(l.application_id) === idNum
    );
    if (byApp?.license_number) {
      return String(byApp.license_number).trim();
    }
    const active = dtl.find((l: any) => l?.is_active === true && l?.license_number != null && String(l.license_number).trim() !== '');
    if (active?.license_number) {
      return String(active.license_number).trim();
    }
    const first = dtl.find((l: any) => l?.license_number != null && String(l.license_number).trim() !== '');
    return first?.license_number != null ? String(first.license_number).trim() : undefined;
  }

  private buildSelectedAppForOpenFromLookup(internalId: string, gridRow?: any): ApplicationOpenGridRow {
    const apps = Array.isArray(this.row?.p_application) ? this.row!.p_application! : [];
    const appMatch = apps.find((a: any) => String(a.application_id) === String(internalId));
    const rawDisplay =
      gridRow?.application_id != null ? String(gridRow.application_id).trim() : '';
    const displayApp =
      rawDisplay !== '' && rawDisplay !== '---'
        ? rawDisplay
        : appMatch?.app_id != null && String(appMatch.app_id).trim() !== ''
          ? String(appMatch.app_id).trim()
          : String(internalId);
    const idNumeric = normalizeNumericApplicationId(internalId);
    const licenseRaw = this.licenseNumberForOpenFromLookup(internalId, gridRow);
    return {
      id: idNumeric !== null ? idNumeric : internalId,
      application_id: displayApp,
      application_number: gridRow?.application_number ?? appMatch?.application_number ?? displayApp,
      application_type: (gridRow?.application_type ?? appMatch?.app_type ?? '') as string,
      date_submitted: (gridRow?.date_submitted ?? appMatch?.date_submitted ?? '') as string,
      re_submitted_date: (gridRow?.resubmitted_date ?? appMatch?.re_submitted_date) as string | undefined,
      application_status: (gridRow?.application_status ?? appMatch?.app_status ?? '') as string,
      educator_name: (gridRow?.educator_name ?? appMatch?.educator_name ?? this.profileName()) as string,
      is_archived: gridRow?.is_archived as boolean | undefined,
      ...(licenseRaw ? { license_number: licenseRaw } : {})
    };
  }

  private formatGridDate(value: unknown): string {
    if (value == null || value === '') return '';
    const d = value instanceof Date ? value : new Date(String(value));
    if (Number.isNaN(d.getTime())) return String(value);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }

  private normalizeStr(val: unknown): string {
    if (val === null || val === undefined) return '';
    return String(val).toLowerCase().trim().replace(/\s+/g, ' ');
  }

  private isNevadaTestDuplicate(form: FormGroup): boolean {
    const fv = form.value;
    const testNameDisplay = this.testNameOptions.find(o => o.key === fv.testName)?.value ?? fv.testName;
    const list: any[] = Array.isArray(this.row?.p_nevada_testing) ? this.row!.p_nevada_testing! : [];
    console.log('[NV Dup] form:', { testName: fv.testName, testNameDisplay, testDate: fv.testDate, score: fv.score });
    return list.some((t: any) => {
      const rowDate = this.formatGridDate(t.test_date);
      const match =
        (this.normalizeStr(t.test_name) === this.normalizeStr(fv.testName) ||
         this.normalizeStr(t.test_name) === this.normalizeStr(testNameDisplay)) &&
        this.normalizeStr(rowDate) === this.normalizeStr(fv.testDate) &&
        String(t.score) === String(fv.score);
      console.log('[NV Dup] row:', { test_name: t.test_name, test_date: t.test_date, rowDate, score: t.score }, '→ match:', match);
      return match;
    });
  }

  private isOosTestDuplicate(form: FormGroup): boolean {
    const fv = form.value;
    const stateDisplay = this.oosTestStateOptions.find(o => o.key === fv.state)?.value ?? fv.state;
    const resultDisplay = this.oosResultOptions.find(o => o.key === fv.result)?.value ?? fv.result;
    const list: any[] = Array.isArray(this.row?.p_out_of_state_testing) ? this.row!.p_out_of_state_testing! : [];
    console.log('[OOS Dup] form:', { state: fv.state, stateDisplay, testName: fv.testName, testDate: fv.testDate, score: fv.score, result: fv.result, resultDisplay });
    return list.some((t: any) => {
      const rowDate = this.formatGridDate(t.test_date);
      const rawResult = t.cd_result ?? t.result ?? '';
      const match =
        (this.normalizeStr(t.state) === this.normalizeStr(fv.state) ||
         this.normalizeStr(t.state) === this.normalizeStr(stateDisplay)) &&
        this.normalizeStr(t.test_name) === this.normalizeStr(fv.testName) &&
        this.normalizeStr(rowDate) === this.normalizeStr(fv.testDate) &&
        String(t.score) === String(fv.score) &&
        (this.normalizeStr(rawResult) === this.normalizeStr(fv.result) ||
         this.normalizeStr(rawResult) === this.normalizeStr(resultDisplay));
      console.log('[OOS Dup] row:', { state: t.state, test_name: t.test_name, test_date: t.test_date, rowDate, score: t.score, cd_result: t.cd_result, result: t.result }, '→ match:', match);
      return match;
    });
  }

  /** Milliseconds since epoch for sorting; invalid/missing => 0. */
  private uploadTimeMs(value: unknown): number {
    if (value == null || value === '') return 0;
    const d = value instanceof Date ? value : new Date(String(value));
    const t = d.getTime();
    return Number.isNaN(t) ? 0 : t;
  }

  private rebuildGridsAndCompliance(): void {
    if (!this.metadataHeadersLoaded) {
      this.loadActivitiesHeaderMetadata();
      return;
    }
    this.preloadSupportingDocTypesForDocuments()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(() => this.rebuildGridsAndComplianceSync());
  }

  private rebuildGridsAndComplianceSync(): void {
    this.rebuildComplianceDisclosures();
    this.rebuildApplicationHistoryGrid();
    this.rebuildNevadaTestingGrid();
    this.rebuildNevadaSupportingDocsGrid();
    this.rebuildOosTestingGrid();
    this.rebuildOosSupportingDocsGrid();
    this.rebuildComplianceSupportingDocsGrid();
    this.rebuildBackgroundCheckGrid();
    this.rebuildProfileDocumentsGrid();
  }

  private loadActivitiesHeaderMetadata(): void {
    const url = LookupUserActivitiesTabComponent.ACTIVITIES_HEADERS_JSON;
    forkJoin({
      applicationHistory: this.commonService.getHeaderData('applicationHistory', url),
      nevadaTesting: this.commonService.getHeaderData('nevadaTesting', url),
      oosTesting: this.commonService.getHeaderData('oosTesting', url),
      supportingDocs: this.commonService.getHeaderData('supportingDocs', url),
      backgroundCheck: this.commonService.getHeaderData('backgroundCheck', url),
      profileDocuments: this.commonService.getHeaderData('profileDocuments', url)
    }).pipe(take(1), takeUntil(this.destroy$)).subscribe({
      next: (headers: any) => {
        this.activitiesMetadataHeaders = headers || {};
        this.metadataHeadersLoaded = true;
        this.rebuildGridsAndCompliance();
      },
      error: () => {
        this.metadataHeadersLoaded = true;
        this.rebuildGridsAndCompliance();
      }
    });
  }

  private static readonly DISCLOSURE_FIELDS: { flag: string; desc: string; label: string }[] = [
    { flag: 'has_cert_or_license_revoked', desc: 'cert_or_license_revoked_desc', label: 'Have you ever had a professional certificate or license revoked, suspended, or denied for cause in any state?' },
    { flag: 'has_suspended_or_revoked_license', desc: 'suspended_revoked_desc', label: 'Do you hold an educator license in any state that is currently suspended or revoked?' },
    { flag: 'has_disciplinary_action', desc: 'disciplinary_action_desc', label: 'Have you ever received a reprimand or other disciplinary action involving any professional certification or license?' },
    { flag: 'has_criminal_conviction', desc: 'criminal_conviction_desc', label: 'Have you ever been convicted of any criminal offense other than a traffic violation?' },
    { flag: 'has_sexual_offense_arrest', desc: 'sexual_offense_arrest_desc', label: 'Have you ever been arrested for any sexual offense involving a minor or pupil?' },
    { flag: 'has_child_abuse_substantiated', desc: 'child_abuse_substantiated_desc', label: 'Has an allegation of child abuse ever been substantiated against you by a law enforcement agency, child welfare agency, or court?' }
  ];

  private rebuildComplianceDisclosures(): void {
    const raw = this.row?.p_background_check as any;
    const out: Array<{ key: string; question: string; yes: boolean | null; description: string }> = [];
    const item = Array.isArray(raw)
      ? (raw.length > 0 ? raw[0] : null)
      : (raw && typeof raw === 'object' ? raw : null);
    LookupUserActivitiesTabComponent.DISCLOSURE_FIELDS.forEach((field, i) => {
      if (item) {
        const ans = item[field.flag];
        const yes: boolean | null =
          ans === true || ans === 'Y' || ans === 'y' ||
          String(ans).toLowerCase() === 'true' || String(ans) === '1'
            ? true
            : ans === false || ans === 'N' || ans === 'n' ||
              String(ans).toLowerCase() === 'false' || String(ans) === '0'
              ? false
              : null;
        const description = field.desc ? String(item[field.desc] ?? '') : '';
        out.push({ key: `d-${i}`, question: field.label, yes, description });
      } else {
        out.push({ key: `d-${i}`, question: field.label, yes: null, description: '' });
      }
    });
    this.complianceDisclosures = out;
  }

  /** Map cd_application_status to queues tab id for App ID icon rules (same as queues module). */
  private mapCdStatusToQueueIconTab(cd: unknown): string {
    const k = String(cd ?? '').trim();
    if (k === '18') return queues[3];
    if (k === '19') return queues[4];
    if (k === '21' || k === '40') return queues[2];
    return 'default';
  }

  private buildAppIdGuidanceTooltips(): Record<string, string> {
    const g = this.guidanceMessages;
    return {
      G0029: (g['G0029'] && String(g['G0029']).trim()) || APP_ID_ICON_GUIDANCE_FALLBACKS.G0029,
      G0030: (g['G0030'] && String(g['G0030']).trim()) || APP_ID_ICON_GUIDANCE_FALLBACKS.G0030,
      G0032: (g['G0032'] && String(g['G0032']).trim()) || APP_ID_ICON_GUIDANCE_FALLBACKS.G0032
    };
  }

  private deriveArchivalStatus(isArchived: unknown): string {
    if (isArchived === true || isArchived === 1 || isArchived === '1') return 'Archived';
    if (typeof isArchived === 'string') {
      const s = isArchived.trim().toLowerCase();
      if (s === 'true' || s === 't') return 'Archived';
    }
    return 'Active';
  }

  private rebuildApplicationHistoryGrid(): void {
    const apps = Array.isArray(this.row?.p_application) ? this.row!.p_application! : [];
    const nameFallback = this.profileName();
    const rows = apps.map((a: any) => {
      const internalId =
        a.application_id != null && String(a.application_id).trim() !== ''
          ? String(a.application_id).trim()
          : '';
      const displayId =
        a.app_id != null && String(a.app_id).trim() !== ''
          ? String(a.app_id).trim()
          : a.application_number != null && String(a.application_number).trim() !== ''
            ? String(a.application_number).trim()
            : internalId;
      const isBg = a.bg_required === true;
      return {
        id: internalId,
        application_id: displayId || '---',
        application_number: displayId,
        case_id: isBg ? (a.case_id ?? a.caseId ?? '') : '',
        application_type: a.app_type ?? a.application_type ?? '',
        license_path: a.license_path ?? null,
        educator_name: a.educator_name ?? nameFallback,
        date_submitted: this.formatGridDate(a.date_submitted ?? a.date_created),
        resubmitted_date: this.formatGridDate(a.resubmitted_date ?? a.date_resubmitted ?? a.re_submitted_date),
        due_date: this.formatGridDate(a.due_date),
        fingerprint_submission_date: isBg ? this.formatGridDate(a.fingerprint_submission_date) : '',
        archival_date: a.archival_date ?? null,
        is_archived: a.is_archived ?? null,
        lockstatus: a.lockstatus ?? '',
        locked_by: a.locked_by ?? '',
        archival_status: this.deriveArchivalStatus(a.is_archived),
        application_status: resolveLookupApplicationStatusDisplay(a, this.row),
        bg_required: isBg,
        new_document: a.new_document ?? false,
        new_update: a.new_update === true,
        deficiency_addressed: a.deficiency_addressed === true,
        cd_application_status: a.cd_application_status != null ? String(a.cd_application_status) : '',
        _iconTab: this.mapCdStatusToQueueIconTab(a.cd_application_status),
        re_submitted_date: a.re_submitted_date ?? a.date_resubmitted ?? null,
        _raw: a
      };
    });
    this._lastApplicationHistoryRows = rows;

    const rawHeaders = this.activitiesMetadataHeaders['applicationHistory'] || [];
    const headers = this.canViewCompliance
      ? rawHeaders
      : rawHeaders.filter((h: any) => h.dataField !== 'case_id');

    this.appHistoryAllColumns = withColumnChooserLock(headersToColumnConfigs(headers));
    this.appHistoryVisibleColumns = mergeVisibleColumnDefaultsWithChooserLock(
      this.appHistoryAllColumns.map(c => c.key),
      this.appHistoryVisibleColumns
    );

    this.applicationHistoryGridConfig = DataGridVO.create({
      customHeaders: headers,
      data: rows,
      showActionsColumn: true,
      showFiltering: true,
      showSearchRow: true,
      showPaging: true,
      customPageSize: 10,
      customNoDataText: this.activityGridNoRecordsText,
      role: 'registration',
      activeTab: 'default',
      showQueueActions: false,
      disableApplicationClick: false,
      plainTextEducatorName: true,
      showNewDocumentIcon: true,
      showNewUploadIcon: true,
      guidanceTooltips: this.buildAppIdGuidanceTooltips(),
      visibleColumns: { ...this.appHistoryVisibleColumns },
      _changeKey: Date.now()
    });
  }

  private profileUploadDocuments(): any[] {
    return profileUploadDocumentsFromDashboardRow(this.row as Record<string, unknown> | null);
  }

  private getProfileDocumentLabelContext(): ProfileDocumentLabelContext {
    const typesByCategory: Record<string, SupportingDocumentGridOption[]> = {
      ...this.supportingDocTypesCache
    };
    // Seed Nevada/OOS lookup key→label maps so Communication-uploaded keys (e.g. 298) resolve in Documents grids
    const nvKey = this.nvSupportingDocCategory?.key;
    if (nvKey && this.testNameOptions?.length) {
      typesByCategory[nvKey] = this.testNameOptions.map(o => ({
        key: String(o.key),
        value: String(o.value ?? '').trim()
      }));
    }
    const oosKey = this.oosSupportingDocCategory?.key;
    if (oosKey && this.oosTestStateOptions?.length) {
      typesByCategory[oosKey] = this.oosTestStateOptions.map(o => ({
        key: String(o.key),
        value: String(o.value ?? '').trim()
      }));
    }
    return {
      categories: this.allSupportingDocCategories,
      typesByCategory,
      nevadaCategoryKey: this.nvSupportingDocCategory?.key,
      oosCategoryKey: this.oosSupportingDocCategory?.key,
      nevadaCategoryOptions: this.nvSupportingDocCategoryOptions,
      oosCategoryOptions: this.oosSupportingDocCategoryOptions
    };
  }

  /** Preload System A type options for categories referenced in p_profile_upload (grid-string rows skipped). */
  private preloadSupportingDocTypesForDocuments(): Observable<void> {
    const docs = this.profileUploadDocuments().filter((d) => !this.isDocumentRemovedFromGrid(d));
    const ctx = this.getProfileDocumentLabelContext();
    const missingKeys = uniqueSystemACategoryKeysFromDocuments(docs, ctx).filter(
      (key) => !this.supportingDocTypesCache[key]?.length
    );
    if (!missingKeys.length) {
      return of(void 0);
    }
    const requests = missingKeys.map((key) => {
      const cat = this.allSupportingDocCategories.find((c) => c.key === key);
      if (!cat) {
        return of([] as SupportingDocumentGridOption[]);
      }
      return this.supportingDocLookupService.getTypesByCategory(cat).pipe(
        tap((types) => {
          this.supportingDocTypesCache[key] = types;
        })
      );
    });
    return forkJoin(requests).pipe(map(() => void 0));
  }

  private mapDocumentRows(source: any[]): any[] {
    const apps = Array.isArray(this.row?.p_application) ? this.row!.p_application! : [];
    const labelCtx = this.getProfileDocumentLabelContext();
    return source.map((d: any) => {
      const docIdRaw = d.document_id ?? d.profile_document_upload_id ?? d.documentId;
      const document_id =
        docIdRaw !== null && docIdRaw !== undefined && String(docIdRaw).trim() !== ''
          ? Number(docIdRaw)
          : NaN;
      const uploadedBy = d.uploaded_by ?? d.uploadedBy ?? '';
      const uploadedDateRaw =
        d.uploaded_date ??
        d.uploadedDate ??
        d.uploaded_at ??
        d.uploadedAt;
      const internalId =
        d.application_id != null && String(d.application_id).trim() !== ''
          ? String(d.application_id).trim()
          : '';
      const matchedApp = apps.find((a: any) => String(a.application_id) === internalId);
      const displayId =
        d.application_number != null && String(d.application_number).trim() !== ''
          ? String(d.application_number).trim()
          : matchedApp?.app_id != null && String(matchedApp.app_id).trim() !== ''
            ? String(matchedApp.app_id).trim()
            : internalId;
      const docRecord = { ...d, _raw: d } as Record<string, unknown>;
      const document_category = resolveProfileDocumentCategoryLabel(docRecord, labelCtx);
      let document_type = resolveProfileDocumentTypeLabel(docRecord, labelCtx);
      // Resolve numeric type key to provider/district name for education sub-sections
      const docSection = String(d.profile_tab_section ?? '').trim();
      if (docSection === 'Additional_prof_dev' || docSection === 'Additional Professional Development') {
        const rawTypeKey = String(d.cd_document_type ?? d.document_type ?? d.type ?? '').trim();
        const fromProviders = this.profDevProvidersLookup.find(p => p.key === rawTypeKey);
        if (fromProviders) document_type = fromProviders.value;
      } else if (docSection === 'employment_prof_dev' || docSection === 'District / Charter Employment') {
        const rawTypeKey = String(d.cd_document_type ?? d.document_type ?? d.type ?? '').trim();
        const fromDistricts = this.districtsLookup.find(p => p.key === rawTypeKey);
        if (fromDistricts) document_type = fromDistricts.value;
      }
      return {
        id: internalId,
        application_id: displayId || '---',
        document_category,
        document_type,
        file_name: d.file_name ?? '',
        uploaded_by: uploadedBy,
        uploaded_date: this.formatGridDate(uploadedDateRaw),
        document_status: d.status ?? d.document_status ?? '',
        document_id: Number.isFinite(document_id) ? document_id : null,
        _raw: d
      };
    });
  }

  /**
   * Nevada vs OOS Supporting Documents: prefer SP `activities_subsection` / `profile_tab_section`;
   * else category/type substring heuristics (see plan).
   */
  private filterDocumentsForActivitiesSlice(documents: any[], slice: 'nevada' | 'oos'): any[] {
    if (!documents.length) return [];
    const hasMeta = documents.some(
      d => d?.activities_subsection != null || d?.profile_tab_section != null
    );
    const lower = (x: unknown) => String(x ?? '').toLowerCase();
    const isOosMeta = (d: any) => {
      const a = lower(d.activities_subsection);
      const p = lower(d.profile_tab_section);
      return isOosSupportingSection(a) || isOosSupportingSection(p);
    };
    if (hasMeta) {
      if (slice === 'nevada') {
        return documents.filter(d => !isOosMeta(d));
      }
      return documents.filter(d => isOosMeta(d));
    }
    const blob = (d: any) =>
      `${lower(d.category ?? d.document_category ?? d.category_name)} ${lower(d.type ?? d.document_type)}`;
    const looksOos = (d: any) => {
      const b = blob(d);
      return b.includes('out of state') || b.includes('out-of-state') || b.includes('out_of_state');
    };
    if (slice === 'nevada') {
      return documents.filter(d => !looksOos(d));
    }
    return documents.filter(d => looksOos(d));
  }

  /**
   * Nevada / OOS Supporting Documents under testing: subsection uploads only (lookup cat 2 / 3 for OOS)
   * or matching profile_tab_section — not unrelated application documents.
   */
  private filterSupportingDocsForTestingSection(documents: any[], section: 'nevada' | 'oos'): any[] {
    if (!documents.length) return [];
    const wantCat = this.supportingDocumentCategoryKey(section);
    const lower = (x: unknown) => String(x ?? '').toLowerCase().replace(/\s+/g, ' ').trim();
    /**
     * Exclude Out-of-State Licensure documents from OOS Testing section.
     * These belong to Experience tab, not Activities → Other Testing.
     * Check both profile_tab_section and document_category.
     */
    const isLicensureDocument = (d: any): boolean => {
      const p = lower(d.profile_tab_section);
      const cat = lower(d.document_category ?? d.category ?? d.category_name ?? '');
      const sectionIsLicensure = (
        p.includes('licensure') ||
        p === 'out of state or country licensure' ||
        p === 'out-of-state-licensure'
      );
      const categoryIsLicensure = (
        cat === 'out-of-state or country licensure' ||
        cat === 'out of state or country licensure' ||
        (cat.includes('licensure') && (cat.includes('out of state') || cat.includes('out-of-state') || cat.includes('out_of_state')))
      );
      return sectionIsLicensure || categoryIsLicensure;
    };
    const profileMatchesSection = (d: any) => {
      const p = lower(d.profile_tab_section);
      if (section === 'nevada') {
        return isNevadaSupportingSection(p);
      }
      // For OOS testing: include OOS supporting but exclude licensure documents
      return isOosSupportingSection(p) && !isLicensureDocument(d);
    };
    const categoryKey = (d: any) => String(d.cd_document_category ?? d.category ?? '').trim();
    const categoryMatchesSection = (d: any) => {
      const ck = categoryKey(d);
      if (section === 'nevada') {
        return ck === wantCat;
      }
      /* OOS supporting: canonical category key **3**; **10** kept for legacy rows only. */
      /* Also exclude licensure documents from OOS Testing section. */
      return this.isOosSupportingCategoryKey(ck) && !isLicensureDocument(d);
    };
    return documents.filter(d => categoryMatchesSection(d) || profileMatchesSection(d));
  }

  private supportingDocsHeaders(): any[] {
    return this.activitiesMetadataHeaders['supportingDocs'] || [];
  }

  private resolveResultFromExamRequirements(testName: string, score: number): string {
    if (!testName || isNaN(score)) return '';
    const rule = this.examRequirements.find((r: any) => {
      const combined = `${String(r.objCategoryData?.strExamCd ?? '').trim()} - ${String(r.objCategoryData?.strExamName ?? '').trim()}`;
      return combined === testName.trim();
    });
    if (!rule) return '';
    const passCd = Number(rule.objCategoryData?.strPassCd);
    if (isNaN(passCd)) return '';
    return score >= passCd ? 'Pass' : 'Fail';
  }

  private rebuildNevadaTestingGrid(): void {
    const list = Array.isArray(this.row?.p_nevada_testing) ? this.row!.p_nevada_testing! : [];
    const rows = list.map((t: any, i: number) => {
      const testName = t.test_name ?? '';
      const score = t.score != null ? Number(t.score) : NaN;
      const computedResult = this.resolveResultFromExamRequirements(testName, score);
      const storedResult =
        t.test_result_nevada ??
        t.test_result_neveda ??
        t.cd_result ??
        t.result ??
        '';
      return {
        id: String(this.resolveNevadaTestNumericId(t) ?? `nv-${i}`),
        test_name: testName,
        test_date: this.formatGridDate(t.test_date),
        score: t.score != null ? String(t.score) : '',
        result: computedResult || storedResult,
        updated_by: t.updated_by || '---',
        _raw: t
      };
    });
    const headers = this.activitiesMetadataHeaders['nevadaTesting'] || [];
    this.nevadaTestingAllColumns = withColumnChooserLock(headersToColumnConfigs(headers));
    this.nevadaTestingVisibleColumns = mergeVisibleColumnDefaultsWithChooserLock(
      this.nevadaTestingAllColumns.map(c => c.key),
      this.nevadaTestingVisibleColumns
    );
    this.nevadaTestingGridConfig = DataGridVO.create({
      customHeaders: headers,
      data: rows,
      showActionsColumn: true,
      showFiltering: true,
      showSearchRow: true,
      showPaging: true,
      customPageSize: 10,
      customNoDataText: this.activityGridNoRecordsText,
      role: 'registration',
      activeTab: 'default',
      showQueueActions: false,
      disableApplicationClick: true,
      visibleColumns: { ...this.nevadaTestingVisibleColumns },
      _changeKey: Date.now()
    });
  }

  private rebuildNevadaSupportingDocsGrid(): void {
    const combined = this.profileUploadDocuments().filter(d => !this.isDocumentRemovedFromGrid(d));
    const filtered = this.filterSupportingDocsForTestingSection(
      this.filterDocumentsForActivitiesSlice(combined, 'nevada'),
      'nevada'
    );
    const rows = this.mapDocumentRows(filtered);
    const nvDocHeaders = this.supportingDocsHeaders();
    this.nvSupportingDocsAllColumns = withColumnChooserLock(headersToColumnConfigs(nvDocHeaders));
    this.nvSupportingDocsVisibleColumns = mergeVisibleColumnDefaultsWithChooserLock(
      this.nvSupportingDocsAllColumns.map(c => c.key),
      this.nvSupportingDocsVisibleColumns
    );
    this.nevadaSupportingDocsGridConfig = DataGridVO.create({
      customHeaders: nvDocHeaders,
      data: rows,
      showActionsColumn: true,
      showFiltering: true,
      showSearchRow: true,
      showPaging: true,
      customPageSize: 10,
      customNoDataText: this.activityGridNoRecordsText,
      role: 'registration',
      activeTab: 'default',
      showQueueActions: false,
      disableApplicationClick: false,
      visibleColumns: { ...this.nvSupportingDocsVisibleColumns },
      _changeKey: Date.now()
    });
  }

  /** Map `cd_result` / `result` codes to OUT_OF_STATE_RESULT lookup labels for grid display. */
  private resolveOosResultDisplayCode(cd: unknown): string {
    const normalized = String(cd ?? '').trim();
    if (normalized === '') return '';
    const hit = this.oosResultOptions.find(o => String(o.key).trim() === normalized);
    if (hit?.value != null && String(hit.value).trim() !== '') {
      return String(hit.value);
    }
    return normalized;
  }

  private rebuildOosTestingGrid(): void {
    const list = Array.isArray(this.row?.p_out_of_state_testing) ? this.row!.p_out_of_state_testing! : [];
    const rows = list.map((t: any, i: number) => ({
      id: String(this.resolveOosTestNumericId(t) ?? `oos-${i}`),
      state: t.state ?? '',
      test_name: t.test_name ?? '',
      test_date: this.formatGridDate(t.test_date),
      score: t.score != null ? String(t.score) : '',
      result: this.resolveOosResultDisplayCode(t.cd_result ?? t.result),
      updated_by: t.updated_by || '---',
      _raw: t
    }));
    const headers = this.activitiesMetadataHeaders['oosTesting'] || [];
    this.oosTestingAllColumns = withColumnChooserLock(headersToColumnConfigs(headers));
    this.oosTestingVisibleColumns = mergeVisibleColumnDefaultsWithChooserLock(
      this.oosTestingAllColumns.map(c => c.key),
      this.oosTestingVisibleColumns
    );
    this.oosTestingGridConfig = DataGridVO.create({
      customHeaders: headers,
      data: rows,
      showActionsColumn: true,
      showFiltering: true,
      showSearchRow: true,
      showPaging: true,
      customPageSize: 10,
      customNoDataText: this.activityGridNoRecordsText,
      role: 'registration',
      activeTab: 'default',
      showQueueActions: false,
      disableApplicationClick: true,
      visibleColumns: { ...this.oosTestingVisibleColumns },
      _changeKey: Date.now()
    });
  }

  private rebuildOosSupportingDocsGrid(): void {
    const combined = this.profileUploadDocuments().filter(d => !this.isDocumentRemovedFromGrid(d));
    const filtered = this.filterSupportingDocsForTestingSection(
      this.filterDocumentsForActivitiesSlice(combined, 'oos'),
      'oos'
    );
    const rows = this.mapDocumentRows(filtered);
    const oosDocHeaders = this.supportingDocsHeaders();
    this.oosSupportingDocsAllColumns = withColumnChooserLock(headersToColumnConfigs(oosDocHeaders));
    this.oosSupportingDocsVisibleColumns = mergeVisibleColumnDefaultsWithChooserLock(
      this.oosSupportingDocsAllColumns.map(c => c.key),
      this.oosSupportingDocsVisibleColumns
    );
    this.oosSupportingDocsGridConfig = DataGridVO.create({
      customHeaders: oosDocHeaders,
      data: rows,
      showActionsColumn: true,
      showFiltering: true,
      showSearchRow: true,
      showPaging: true,
      customPageSize: 10,
      customNoDataText: this.activityGridNoRecordsText,
      role: 'registration',
      activeTab: 'default',
      showQueueActions: false,
      disableApplicationClick: false,
      visibleColumns: { ...this.oosSupportingDocsVisibleColumns },
      _changeKey: Date.now()
    });
  }

  private filterDocumentsComplianceHistoryDisclosure(documents: any[]): any[] {
    if (!documents.length) return [];
    const want = LookupUserActivitiesTabComponent.COMPLIANCE_HISTORY_DISCLOSURE_PROFILE_SECTION.toLowerCase();
    const legacy = 'activities_compliance_history_disclosure';
    return documents.filter((d: any) => {
      const p = String(d.profile_tab_section ?? d._raw?.profile_tab_section ?? '').toLowerCase();
      return p === want || p === legacy;
    });
  }

  private rebuildComplianceSupportingDocsGrid(): void {
    const combined = this.profileUploadDocuments().filter(d => !this.isDocumentRemovedFromGrid(d));
    const filtered = this.filterDocumentsComplianceHistoryDisclosure(combined);
    const rows = this.mapDocumentRows(filtered);
    const h = this.supportingDocsHeaders();
    this.complianceSupportingDocsAllColumns = withColumnChooserLock(headersToColumnConfigs(h));
    this.complianceSupportingDocsVisibleColumns = mergeVisibleColumnDefaultsWithChooserLock(
      this.complianceSupportingDocsAllColumns.map(c => c.key),
      this.complianceSupportingDocsVisibleColumns
    );
    this.complianceSupportingDocsGridConfig = DataGridVO.create({
      customHeaders: h,
      data: rows,
      showActionsColumn: true,
      showFiltering: true,
      showSearchRow: true,
      showPaging: true,
      customPageSize: 10,
      customNoDataText: this.activityGridNoRecordsText,
      role: 'registration',
      activeTab: 'default',
      showQueueActions: false,
      disableApplicationClick: false,
      visibleColumns: { ...this.complianceSupportingDocsVisibleColumns },
      _changeKey: Date.now()
    });
  }

  private pickBackgroundRow(raw: Record<string, any>): any {
    const r = raw || {};
    const caseRaw = r.case_id ?? r.caseId ?? r.complaint_form_id;
    const case_id =
      caseRaw != null && String(caseRaw).trim() !== '' ? String(caseRaw).trim() : '';
    return {
      case_id,
      fingerprint_method: r.submission_method ?? r.fingerprint_method ?? r.fingerprintMethod ?? r.cd_fingerprint_method ?? '',
      fingerprint_submission_date: this.formatGridDate(
        r.fingerprint_submission_date ?? r.fingerprintSubmissionDate ?? r.submission_date
      ),
      fingerprint_attempt: r.isreprint != null
        ? (String(r.isreprint).toLowerCase() === 'yes' ? 'Reprint' : 'Initial')
        : (r.fingerprint_attempt ?? r.fingerprintAttempt ?? r.attempt ?? ''),
      bg1_status: r.bg1_cd_decision ?? r.bg1_status ?? r.bg_1_status ?? r.BG1_Status ?? '',
      bg2_status: r.bg2_cd_decision ?? r.bg2_status ?? r.bg_2_status ?? r.BG2_Status ?? '',
      _raw: r
    };
  }

  private rebuildBackgroundCheckGrid(): void {
    const raw = this.row?.p_complaince;
    const list = Array.isArray(raw) ? raw : [];
    const hist = this._lastApplicationHistoryRows || [];
    const rows = list.map((item: any, i: number) => {
      const picked = this.pickBackgroundRow(item as Record<string, any>);
      const appIdRaw = item?.application_id ?? item?.applicationId ?? picked._raw?.application_id;
      const appIdStr =
        appIdRaw != null && String(appIdRaw).trim() !== '' ? String(appIdRaw).trim() : '';
      const caseNorm = String(picked.case_id ?? '').trim();
      const byCase =
        caseNorm !== '' ? hist.find(r => String(r.case_id ?? '').trim() === caseNorm) : undefined;
      const idFromHistory =
        byCase?.id != null && String(byCase.id).trim() !== '' ? String(byCase.id).trim() : '';
      const id = appIdStr || idFromHistory || `bg-${i}`;
      return { id, ...picked };
    });
    const headers = this.activitiesMetadataHeaders['backgroundCheck'] || [];
    this.backgroundCheckAllColumns = withColumnChooserLock(headersToColumnConfigs(headers));
    this.backgroundCheckVisibleColumns = mergeVisibleColumnDefaultsWithChooserLock(
      this.backgroundCheckAllColumns.map(c => c.key),
      this.backgroundCheckVisibleColumns
    );
    this.backgroundCheckGridConfig = DataGridVO.create({
      customHeaders: headers,
      data: rows,
      showActionsColumn: false,
      showFiltering: true,
      showSearchRow: true,
      showPaging: true,
      customPageSize: 10,
      customNoDataText: this.activityGridNoRecordsText,
      role: 'registration',
      activeTab: 'default',
      showQueueActions: false,
      visibleColumns: { ...this.backgroundCheckVisibleColumns },
      _changeKey: Date.now()
    });
  }

  // ── Supporting Documents: View / Download / Upload ──

  /** Dashboard SP may return application_* or profile_* id columns (get_educator_dashboard_data_new). */
  private resolveNevadaTestNumericId(raw: any): number | null {
    for (const k of ['nevada_testing_id', 'profile_nevada_testing_id', 'test_id', 'id']) {
      const n = Number(raw?.[k]);
      if (Number.isFinite(n) && n > 0) return n;
    }
    return null;
  }

  private resolveOosTestNumericId(raw: any): number | null {
    for (const k of ['out_of_state_testing_id', 'profile_out_of_state_testing_id', 'test_id', 'id']) {
      const n = Number(raw?.[k]);
      if (Number.isFinite(n) && n > 0) return n;
    }
    return null;
  }

  /** Nevada Test Name `<select>` uses lookup `key`; dashboard may only return `test_name` label without `cd_test_name`. */
  private resolveNevadaTestNameKeyForForm(raw: any): string {
    const cd = raw?.cd_test_name;
    if (cd != null && String(cd).trim() !== '') {
      return String(cd).trim();
    }
    const label = raw?.test_name;
    if (label == null || String(label).trim() === '') {
      return '';
    }
    const s = String(label).trim();
    const lower = s.toLowerCase();
    const opts = Array.isArray(this.testNameOptions) ? this.testNameOptions : [];
    const byValue = opts.find(o => String(o.value ?? '').trim().toLowerCase() === lower);
    if (byValue) return String(byValue.key);
    const byKey = opts.find(o => String(o.key ?? '').trim() === s);
    if (byKey) return String(byKey.key);
    return '';
  }

  /** State `<select>` uses lookup `key`; dashboard may only return resolved `state` label without `cd_state`. */
  private resolveOosStateKeyForForm(raw: any): string {
    const cd = raw?.cd_state;
    if (cd != null && String(cd).trim() !== '') {
      return String(cd).trim();
    }
    const label = raw?.state;
    if (label == null || String(label).trim() === '') {
      return '';
    }
    const s = String(label).trim();
    const lower = s.toLowerCase();
    const opts = Array.isArray(this.oosTestStateOptions) ? this.oosTestStateOptions : [];
    const byValue = opts.find(o => String(o.value ?? '').trim().toLowerCase() === lower);
    if (byValue) return String(byValue.key);
    const byKey = opts.find(o => String(o.key ?? '').trim() === s);
    if (byKey) return String(byKey.key);
    return '';
  }

  supportingDocumentHasValidId(row: { document_id?: number | null }): boolean {
    const n = Number(row?.document_id);
    return Number.isFinite(n) && n > 0;
  }

  viewSupportingDocument(row: { document_id?: number | null; file_name?: string }): void {
    const id = Number(row?.document_id);
    if (!Number.isFinite(id) || id <= 0) {
      this.alertService.error('Document information is missing');
      return;
    }
    this.httpService
      .post(API_ENDPOINTS.EDUCATOR_COMMUNICATION_DOWNLOAD, {
        profile_document_upload_id: id
      })
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const fileUrl = resolveEducatorDocumentFileUrl(response);
          if (!fileUrl) {
            this.alertService.error('Failed to retrieve document URL');
            return;
          }
          this.documentPreviewModal.openFromUrl(fileUrl, row.file_name);
        },
        error: () => {
          this.alertService.error('Failed to view document. Please try again.');
        }
      });
  }

  downloadSupportingDocument(row: { document_id?: number | null; file_name?: string }): void {
    const id = Number(row?.document_id);
    if (!Number.isFinite(id) || id <= 0) {
      this.alertService.error('Document information is missing');
      return;
    }
    this.httpService
      .post(API_ENDPOINTS.EDUCATOR_COMMUNICATION_DOWNLOAD, {
        profile_document_upload_id: id
      })
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const fileUrl = this.resolveSupportingDocumentFileUrl(response);
          if (!fileUrl) {
            this.alertService.error('Failed to download document');
            return;
          }
          fetch(fileUrl)
            .then(res => res.blob())
            .then(blob => {
              const fileName = String(row.file_name || `document_${id}`);
              FileSaver.saveAs(blob, fileName);
              //this.alertService.success('Document downloaded successfully');
            })
            .catch(() => {
              this.alertService.error('Failed to download document file');
            });
        },
        error: () => {
          this.alertService.error('Failed to download document. Please try again.');
        }
      });
  }

  private resolveSupportingDocumentFileUrl(response: any): string | null {
    return (
      response?.data?.data?.p_document_blob?.file_url ||
      response?.data?.data?.p_document_blob?.fileUrl ||
      response?.data?.data?.p_document_blob?.strfileHash ||
      response?.data?.data?.p_document_blob?.file_hash ||
      response?.data?.data?.p_document_blob?.fileHash ||
      response?.data?.file_url ||
      response?.data?.fileUrl ||
      response?.data?.url ||
      response?.file_url ||
      response?.fileUrl ||
      null
    );
  }

  clearFileInput(event: Event): void {
    (event.target as HTMLInputElement).value = '';
  }

  private getCurrentUserId(): number | null {
    try {
      const userRaw = sessionStorage.getItem('user');
      if (!userRaw) return null;
      const user = JSON.parse(userRaw);
      return user?.user_id ?? user?.id ?? user?.userId ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Returns true when the given category key or label matches "Child Support Disclosure" or "History Disclosure".
   * Documents with these categories should always be saved with status "Case".
   */
  private isCaseDocCategory(catKey: string, catLabel?: string): boolean {
    const normalize = (v: string) => (v ?? '').trim().toLowerCase();
    return normalize(catKey) === 'child support disclosure'
      || normalize(catLabel ?? '') === 'child support disclosure'
      || normalize(catKey) === 'history disclosure'
      || normalize(catLabel ?? '') === 'history disclosure';
  }

  private isCaseDocSection(profileTabSection: string): boolean {
    const normalize = (v: string) => (v ?? '').trim().toLowerCase();
    const normalizedSection = normalize(profileTabSection);
    return normalizedSection === 'history disclosure'
      || normalizedSection === 'child support disclosure';
  }

  // ── Document action handlers ──

  viewProfileDocument(row: any): void {
    this.viewSupportingDocument(row);
  }

  downloadProfileDocument(row: any): void {
    this.downloadSupportingDocument(row);
  }

  openEditDocumentModal(row: any): void {
    this.editDocRow = row;
    this.editDocSupportingSection = this.resolveSupportingSectionForEditDoc(row);
    this.editDocProfileTabSection = this.resolveProfileTabSection(row);
    const rawPts = String(row?._raw?.profile_tab_section ?? row?.profile_tab_section ?? '').trim();
    this.editDocUsesSystemA = !this.editDocProfileTabSection && !this.editDocSupportingSection;
    this.editDocSystemASectionName = this.editDocUsesSystemA
      ? this.resolveSystemASectionNameForEdit(rawPts)
      : null;
    this.editDocCategoryOptions = this.editDocUsesSystemA ? [] : this.buildEditDocCategoryOptions();
    const rawCat = row?._raw?.cd_document_category ?? row?._raw?.document_category ?? row?._raw?.category ?? '';
    const rawType = row?._raw?.cd_document_type ?? row?._raw?.document_type ?? row?._raw?.type ?? '';
    const rawTypeTrim = String(rawType).trim();
    let catKey = String(rawCat).trim();
    if (this.editDocSupportingSection === 'oos') {
      catKey = this.supportingDocumentCategoryKey('oos');
    } else if (this.editDocSupportingSection === 'nevada') {
      catKey = this.supportingDocumentCategoryKey('nevada');
    }
    const typeKey = this.editDocSupportingSection
      ? rawTypeTrim
      : this.editDocProfileTabSection
        ? (rawTypeTrim || String(row?.document_type ?? '').trim())
        : rawTypeTrim;
    const notes = row?._raw?.document_notes ?? '';
    if (this.editDocUsesSystemA) {
      this.editDocForm.patchValue({ category: catKey, type: typeKey, notes });
      this.loadEditDocSystemACategoryOptions(catKey, typeKey);
    } else if (this.editDocProfileTabSection) {
      const catLabel = String(row?.document_category ?? '').trim();
      const matchedCat = this.editDocCategoryOptions.find(
        (c) => c.key === catKey || c.value === catLabel || c.key === catLabel
      );
      // If only one option exists (fixed section), always use it
      if (!matchedCat && this.editDocCategoryOptions.length === 1) {
        catKey = this.editDocCategoryOptions[0].key;
      } else {
        catKey = matchedCat?.key ?? (catLabel || catKey);
      }
      this.editDocForm.patchValue({ category: catKey, type: typeKey, notes });
      this.onEditDocCategoryChange(catKey, typeKey);
    } else if (this.editDocSupportingSection) {
      this.filteredDocTypeOptions = this.buildSupportingEditTypeOptions(this.editDocSupportingSection, rawTypeTrim);
      this.editDocForm.patchValue({ category: catKey, type: typeKey, notes });
    }
    this.editDocFormSnapshot = this.editDocForm.getRawValue();
    this.editDocModalRef = this.ngbService.open(this.editDocumentModal, {
      size: 'lg', centered: true, backdrop: 'static', keyboard: false,
      windowClass: 'activities-doc-modal custom-activities-doc-modal'
    });
  }

  onEditDocCategoryChange(categoryKey?: string, preserveType?: string): void {
    const key = categoryKey ?? this.editDocForm.value.category;
    if (!key) {
      this.filteredDocTypeOptions = [];
      return;
    }
    if (this.editDocUsesSystemA) {
      const selected = (this.editDocCategoryOptions as any[]).find((c: any) => c.key === key);
      if (selected) {
        this.supportingDocLookupService.getTypesByCategory(selected as SupportingDocumentGridOption)
          .pipe(take(1), takeUntil(this.destroy$))
          .subscribe((types: SupportingDocumentGridOption[]) => {
            this.filteredDocTypeOptions = types as any;
            this.supportingDocTypesCache[key] = types;
          });
      } else {
        this.filteredDocTypeOptions = [];
      }
      if (!preserveType) {
        this.editDocForm.patchValue({ type: '' });
      }
      return;
    }
    if (this.editDocProfileTabSection) {
      const curType = preserveType ?? String(this.editDocForm.value.type ?? '').trim();
      // For sections that use System A category/type, load types from System A
      const usesSystemATypes =
        this.editDocProfileTabSection === 'academic-Background' ||
        this.editDocProfileTabSection === 'employment_prof_dev' ||
        this.editDocProfileTabSection === 'Additional_prof_dev';
      if (usesSystemATypes) {
        if (this.editDocProfileTabSection === 'academic-Background') {
          const ctx = this.getProfileDocumentLabelContext();
          if (isAcademicBackgroundInstitutionCategory(key, ctx)) {
            this.filteredDocTypeOptions = this.buildEducationExpTypeOptions(
              this.editDocProfileTabSection,
              curType,
              key
            );
            if (!preserveType) {
              this.editDocForm.patchValue({ type: '' });
            }
            return;
          }
          // academic-Background non-institution: resolve from allSupportingDocCategories by key
          const selected: SupportingDocumentGridOption | undefined =
            this.allSupportingDocCategories.find((c) => c.key === key) ||
            this.allSupportingDocCategories.find((c) => c.value === key) ||
            (this.editDocCategoryOptions as any[]).find((c: any) => c.key === key);
          if (selected) {
            this.supportingDocLookupService
              .getTypesByCategory(selected as SupportingDocumentGridOption)
              .pipe(take(1), takeUntil(this.destroy$))
              .subscribe((types: SupportingDocumentGridOption[]) => {
                this.supportingDocTypesCache[key] = types;
                this.filteredDocTypeOptions = types as any;
                if (preserveType) {
                  const matchedType = types.find((t) => t.key === preserveType || t.value === preserveType);
                  this.editDocForm.patchValue({ type: matchedType?.key ?? '' });
                } else {
                  this.editDocForm.patchValue({ type: '' });
                }
              });
          } else {
            this.filteredDocTypeOptions = [];
          }
          return;
        }
        // employment_prof_dev: district/charter names from educator's p_prof_dev_district_charter rows
        // Additional_prof_dev: provider names from educator's p_prof_dev_additional rows
        const names: string[] =
          this.editDocProfileTabSection === 'employment_prof_dev'
            ? ([...new Set<string>(
                ((this.row as any)?.p_prof_dev_district_charter ?? [])
                  .map((r: any) => String(r?.district_charter_name ?? '').trim())
                  .filter((n: string) => !!n)
              )] as string[]).sort()
            : ([...new Set<string>(
                ((this.row as any)?.p_prof_dev_additional ?? [])
                  .map((r: any) => String(r?.provider_name ?? '').trim())
                  .filter((n: string) => !!n)
              )] as string[]).sort();
        const cur = String(preserveType ?? '').trim();
        if (cur && !names.includes(cur)) {
          names.push(cur);
          names.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
        }
        this.filteredDocTypeOptions = names.map(n => ({ id: 0, parentid: null, key: n, value: n })) as any;
        this.editDocForm.patchValue({ type: cur || '' });
        return;
      }
      this.filteredDocTypeOptions = this.buildEducationExpTypeOptions(this.editDocProfileTabSection, curType);
      if (!preserveType) {
        this.editDocForm.patchValue({ type: '' });
      }
      return;
    }
    if (this.editDocSupportingSection) {
      const typeForList =
        preserveType !== undefined && String(preserveType).trim().length > 0
          ? String(preserveType).trim()
          : String(this.editDocForm.value.type ?? '').trim();
      this.filteredDocTypeOptions = this.buildSupportingEditTypeOptions(this.editDocSupportingSection, typeForList);
      if (!preserveType) {
        this.editDocForm.patchValue({ type: '' });
      }
    }
  }

  closeEditDocumentModal(): void {
    this.editDocModalRef?.close();
    this.editDocModalRef = null;
    this.editDocRow = null;
    this.editDocSupportingSection = null;
    this.editDocProfileTabSection = null;
    this.editDocUsesSystemA = false;
    this.editDocSystemASectionName = null;
    this.editDocFormSnapshot = null;
    this.editDocCategoryOptions = [];
  }

  onResetEditDocumentForm(): void {
    const snap = this.editDocFormSnapshot;
    if (!snap) {
      return;
    }
    this.editDocForm.patchValue(snap as { category: string; type: string; notes: string });
    this.onEditDocCategoryChange(String(snap['category'] ?? ''), String(snap['type'] ?? ''));
    this.editDocForm.markAsPristine();
    this.editDocForm.markAsUntouched();
  }

  onUpdateDocument(): void {
    if (!this.editDocRow) return;
    if (this.editDocForm.invalid) {
      this.editDocForm.markAllAsTouched();
      this.alertService.error(this.toastMessage['T0017'] || 'Please fill in all required fields.');
      return;
    }
    let catKey = String(this.editDocForm.value.category ?? '').trim();
    if (this.editDocSupportingSection === 'oos') {
      catKey = this.supportingDocumentCategoryKey('oos');
    } else if (this.editDocSupportingSection === 'nevada') {
      catKey = this.supportingDocumentCategoryKey('nevada');
    }
    const typeKey = this.editDocForm.value.type;

    const payload = {
      document_id: this.editDocRow.document_id,
      cd_document_category: catKey,
      cd_document_type: typeKey,
      notes: this.editDocForm.value.notes
    };
    this.httpService.post(API_ENDPOINTS.EDUCATOR_UPDATE_DOCUMENT, payload)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          if (res?.success === false) {
            this.alertService.error(res.message || 'Failed to update document.');
            return;
          }
          this.alertService.success(this.toastMessage['T0020'] || 'Document updated successfully.');
          this.closeEditDocumentModal();
          this.requestDashboardRefresh.emit();
        },
        error: () => {
          this.alertService.error('Failed to update document. Please try again.');
        }
      });
  }

  // ── Upload document modal ──

  openUploadDocumentModal(): void {
    this.staffDocNoticeModal.open({ contentVariant: 'default' }).then((confirmed) => {
      if (!confirmed) return;
      this.docUploadFile = null;
      this.docUploadError = null;
      this.docDropzoneActive = false;
      this.uploadFilteredDocTypeOptions = [];
      this.uploadDocCategoryOptions = [];
      this.uploadDocForm.reset({ category: '', type: '', notes: '' });
      this.supportingDocLookupService
        .getAllTopLevelCategories()
        .pipe(take(1), takeUntil(this.destroy$))
        .subscribe((cats: SupportingDocumentGridOption[]) => {
          this.uploadDocCategoryOptions = cats;
        });
      this.uploadDocModalRef = this.ngbService.open(this.uploadDocumentModalTpl, {
        size: 'lg',
        centered: true,
        backdrop: 'static',
        keyboard: false,
        windowClass: 'activities-doc-modal custom-activities-doc-modal'
      });
    });
  }

  /** Same draft semantics as Personal `showCancelUploadConfirmation` for Profile Documents upload. */
  private hasProfileDocumentsUploadDraft(): boolean {
    const v = this.uploadDocForm?.getRawValue() ?? {};
    const notes = String(v.notes ?? '').trim();
    const cat = String(v.category ?? '').trim();
    const typ = String(v.type ?? '').trim();
    return !!(this.docUploadFile || notes || cat || typ);
  }

  /**
   * Matches Personal Name Change `showCancelUploadConfirmation` ModalComponent config.
   */
  private openDiscardUploadDraftModal(onYes: () => void): void {
    const modalRef = this.ngbService.open(ModalComponent, {
      size: 'md',
      centered: true,
      backdrop: 'static'
    });
    modalRef.componentInstance.modalConfig = getCancelFileUploadModalConfig();
    modalRef.result
      .then((result: any) => {
        if (result?.code === 'Y' || result?.confirmed === true) {
          onYes();
        }
      })
      .catch(() => {
        /* dismissed — keep upload modal open */
      });
  }

  onUploadTypeDropdownShow(dropdown: Dropdown): void {
    flipLookupUploadDropdownAbove(dropdown);
  }

  closeUploadDocumentModal(): void {
    if (this.profileDocumentsUploadSaving) {
      return;
    }
    if (this.hasProfileDocumentsUploadDraft()) {
      this.openDiscardUploadDraftModal(() => this._actuallyCloseUploadDocumentModal());
    } else {
      this._actuallyCloseUploadDocumentModal();
    }
  }

  private _actuallyCloseUploadDocumentModal(): void {
    this.uploadDocModalRef?.close();
    this.uploadDocModalRef = null;
    this.docUploadFile = null;
    this.docUploadError = null;
    this.docDropzoneActive = false;
    this.uploadFilteredDocTypeOptions = [];
    this.uploadDocCategoryOptions = [];
    this.uploadDocForm.reset({ category: '', type: '', notes: '' });
  }

  onDocDragOver(event: DragEvent): void {
    event.preventDefault();
    this.docDropzoneActive = true;
  }

  onDocDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.docDropzoneActive = false;
  }

  onDropDocFile(event: DragEvent): void {
    event.preventDefault();
    this.docDropzoneActive = false;
    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      this.setDocUploadFile(event.dataTransfer.files[0]);
    }
  }

  handleDocFileUpload(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    this.setDocUploadFile(input.files[0]);
  }

  clearDocUploadFile(): void {
    this.docUploadFile = null;
    this.docUploadError = null;
  }

  private setDocUploadFile(file: File): void {
    const maxSize = 15 * 1024 * 1024;
    const allowedExt = ['.pdf', '.jpg', '.jpeg', '.png'];
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!allowedExt.includes(ext)) {
      this.docUploadError = this.errorMessage['M0129'];
      this.docUploadFile = null;
      return;
    }
    if (file.size > maxSize) {
      this.docUploadError = this.errorMessage['M0128'];
      this.docUploadFile = null;
      return;
    }
    this.docUploadFile = file;
    this.docUploadError = null;
  }

  onUploadDocCategoryChange(): void {
    const key = this.uploadDocForm.value.category;
    this.uploadDocForm.patchValue({ type: '' });
    if (!key) {
      this.uploadFilteredDocTypeOptions = [];
      return;
    }
    const selectedCat = this.uploadDocCategoryOptions.find(c => c.key === key);
    if (!selectedCat) {
      this.uploadFilteredDocTypeOptions = [];
      return;
    }
    // Nevada / Other Testing: Type = grid test names (same as Supporting upload), not empty CMS children
    const supportingSection = this.resolveUploadDocSupportingSection(selectedCat);
    if (supportingSection) {
      const testNames = this.uniqueSupportingDocTestNames(supportingSection);
      this.uploadFilteredDocTypeOptions = testNames.map(n => ({ key: n, value: n }));
      this.supportingDocTypesCache[key] = this.uploadFilteredDocTypeOptions;
      return;
    }
    // Education / Experience sections: types come from grid data rows (same as Edit modal),
    // not CMS lookup children which may be empty or wrong.
    const sectionName = resolveProfileTabSectionFromLookupCategory(selectedCat, '');
    const allSectionLabels: Record<string, string> = {
      ...PROFILE_DOC_EDUCATION_SECTION_LABELS,
      ...PROFILE_DOC_EXPERIENCE_SECTION_LABELS
    };
    const internalSectionKey = allSectionLabels[sectionName];
    if (internalSectionKey) {
      const built = this.buildEducationExpTypeOptions(internalSectionKey, undefined, key);
      this.uploadFilteredDocTypeOptions = built.map(t => ({ key: t.key, value: t.value }));
      return;
    }
    this.supportingDocLookupService
      .getTypesByCategory(selectedCat)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((types: SupportingDocumentGridOption[]) => {
        this.uploadFilteredDocTypeOptions = types;
        this.supportingDocTypesCache[key] = types;
      });
  }

  /**
   * Detect Nevada/OOS for Documents upload Type options.
   * Use section_name / display label only — never category key.
   * Legacy OOS supporting key `10` collides with High School Diploma CMS category.
   */
  private resolveUploadDocSupportingSection(
    cat: SupportingDocumentGridOption
  ): 'nevada' | 'oos' | null {
    const sectionName = resolveProfileTabSectionFromLookupCategory(cat, '');
    if (isNevadaSupportingSection(sectionName)) {
      return 'nevada';
    }
    const sectionLower = String(sectionName ?? '').trim().toLowerCase();
    if (
      sectionLower.includes('other testing') ||
      (sectionLower.includes('out of state') && sectionLower.includes('testing'))
    ) {
      return 'oos';
    }
    const label = String(cat.value ?? '').trim().toLowerCase();
    if (label.includes('nevada') && (label.includes('praxis') || label.includes('testing'))) {
      return 'nevada';
    }
    if (label.includes('other testing') || (label.includes('out of state') && label.includes('testing'))) {
      return 'oos';
    }
    return null;
  }

  private getProfileDocumentDuplicateLookups(): {
    categoryLookups: ReturnType<typeof toDuplicateLookupOptions>;
    typeLookups: ReturnType<typeof toDuplicateLookupOptions>;
  } {
    return {
      categoryLookups: toDuplicateLookupOptions(this.uploadDocCategoryOptions),
      typeLookups: toDuplicateLookupOptions(this.uploadFilteredDocTypeOptions)
    };
  }

  private cacheProfileUploadAfterSuccess(upload: {
    cd_document_category: string;
    cd_document_type?: string;
    document_category?: string;
    document_type?: string;
    file_name: string;
    profile_tab_section?: string;
    document_status?: string;
    status?: string;
  }): void {
    if (!this.row) {
      return;
    }
    appendProfileUploadToDashboardRow(
      this.row as Record<string, unknown>,
      buildProfileUploadRowForLocalCache(upload)
    );
    this.rebuildProfileDocumentsGrid();
  }

  private async guardProfileDocumentDuplicateUpload(params: {
    categoryKey: string;
    typeKey?: string;
    fileName: string;
    screenName: string;
    categoryDisplay?: string;
    typeDisplay?: string;
  }): Promise<boolean> {
    const personId = this.personId ? Number(this.personId) : 0;
    if (!personId) {
      return true;
    }
    const { categoryLookups, typeLookups } = this.getProfileDocumentDuplicateLookups();
    return this.duplicateDocGuard.ensureProceedOrCancel({
      educatorDashboardRow: this.row as Record<string, unknown> | null,
      categoryKey: params.categoryKey,
      typeKey: params.typeKey,
      fileName: params.fileName,
      screenName: params.screenName,
      personId,
      categoryDisplay: params.categoryDisplay,
      typeDisplay: params.typeDisplay,
      categoryLookups,
      typeLookups
    });
  }

  async onSaveDocumentUpload(): Promise<void> {
    if (!this.docUploadFile) {
      this.docUploadError = 'Please select a file.';
      return;
    }
    if (this.uploadDocForm.invalid) {
      this.uploadDocForm.markAllAsTouched();
      return;
    }
    const catKey = this.uploadDocForm.value.category;
    const typeKey = this.uploadDocForm.value.type;
    const catOpt = this.uploadDocCategoryOptions.find(
      (c) => c.key === catKey
    );
    const typeOpt = this.uploadFilteredDocTypeOptions.find(
      (t) => t.key === typeKey
    );

    const proceed = await this.guardProfileDocumentDuplicateUpload({
      categoryKey: String(catKey ?? ''),
      typeKey: String(typeKey ?? ''),
      fileName: this.docUploadFile.name,
      screenName: 'Lookup User - Activities Documents',
      categoryDisplay: catOpt?.value,
      typeDisplay: typeOpt?.value
    });
    if (!proceed) {
      this.docUploadFile = null;
      this.docUploadError = null;
      return;
    }

    let profileTabSection = resolveProfileTabSectionFromLookupCategory(catOpt);
    const supportingSection = catOpt ? this.resolveUploadDocSupportingSection(catOpt) : null;
    if (supportingSection === 'nevada') {
      profileTabSection = 'Nevada Praxis Testing';
    } else if (supportingSection === 'oos') {
      profileTabSection = 'Other Testing';
    }
    const isChildSupportDisclosure = this.isCaseDocCategory(catKey, catOpt?.value);
    const isCaseDocUpload = isChildSupportDisclosure || this.isCaseDocSection(profileTabSection);

    const uploadData: Record<string, unknown> = {
      person_id: this.personId ? Number(this.personId) : null,
      uploaded_by: this.getCurrentUserId(),
      uploaded_at: new Date().toISOString(),
      file_name: this.docUploadFile.name,
      cd_document_category: catKey,
      cd_document_type: typeKey,
      notes: this.uploadDocForm.value.notes || '',
      profile_tab_section: profileTabSection,
      formid: LOOKUP_USER_SECTION_FORM_IDS[profileTabSection] ?? null,
      ...(isCaseDocUpload ? { document_status: 'Case', status: 'Case' } : {})
    };

    this.profileDocumentsUploadSaving = true;
    educatorDocumentUploadWithClientBlobPut(this.httpService, this.docUploadFile, uploadData)
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          this.profileDocumentsUploadSaving = false;
        })
      )
      .subscribe({
        next: (res: any) => {
          if (res?.success === false) {
            this.alertService.error(res.message || 'Failed to upload document.');
            return;
          }
          this.alertService.success(this.toastMessage['T0005'] || 'Document uploaded successfully.');
          this.cacheProfileUploadAfterSuccess({
            cd_document_category: String(catKey ?? ''),
            cd_document_type: String(typeKey ?? ''),
            document_category: catOpt?.value,
            document_type: typeOpt?.value,
            file_name: this.docUploadFile!.name,
            profile_tab_section: profileTabSection,
            ...(isCaseDocUpload ? { document_status: 'Case', status: 'Case' } : {})
          });
          this._actuallyCloseUploadDocumentModal();
          this.requestDashboardRefresh.emit();
        },
        error: () => {
          this.alertService.error('Failed to upload document. Please try again.');
        }
      });
  }

  // ── Supporting Doc Upload (Nevada / OOS) ──

  /** Legacy OOS supporting uploads: lookup category key **10**; treat as OOS supporting for filters/edit. */
  private static readonly SUPPORTING_DOC_OOS_CATEGORY_KEY_LEGACY = '10';

  /** Resolved category from platform master lookup for Nevada supporting uploads. */
  private nvSupportingDocCategory: SupportingDocumentGridOption | null = null;
  /** Resolved category from platform master lookup for OOS supporting uploads. */
  private oosSupportingDocCategory: SupportingDocumentGridOption | null = null;

  /** True when `cd_document_category` is OOS supporting: resolved key, or legacy **10**. */
  private isOosSupportingCategoryKey(ck: string): boolean {
    const k = String(ck ?? '').trim();
    return (
      (!!this.oosSupportingDocCategory?.key && k === this.oosSupportingDocCategory.key) ||
      k === LookupUserActivitiesTabComponent.SUPPORTING_DOC_OOS_CATEGORY_KEY_LEGACY
    );
  }

  private supportingDocumentCategoryKey(section: 'nevada' | 'oos'): string {
    return section === 'nevada'
      ? (this.nvSupportingDocCategory?.key ?? '')
      : (this.oosSupportingDocCategory?.key ?? '');
  }

  /** Unique non-empty `test_name` values from the Nevada or OOS testing grid payload. */
  private uniqueSupportingDocTestNames(section: 'nevada' | 'oos'): string[] {
    const list =
      section === 'nevada'
        ? Array.isArray(this.row?.p_nevada_testing)
          ? this.row!.p_nevada_testing!
          : []
        : Array.isArray(this.row?.p_out_of_state_testing)
          ? this.row!.p_out_of_state_testing!
          : [];
    const names = list
      .map((t: { test_name?: unknown }) => String(t.test_name ?? '').trim())
      .filter(n => n.length > 0);
    return [...new Set(names)].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }

  /** Nevada/OOS supporting edit: type keys are test names, not CMS document_type children. */
  private supportingSectionFromCategoryKey(catKey: string): 'nevada' | 'oos' | null {
    const k = String(catKey ?? '').trim();
    if (!!this.nvSupportingDocCategory?.key && k === this.nvSupportingDocCategory.key) {
      return 'nevada';
    }
    if (this.isOosSupportingCategoryKey(k)) {
      return 'oos';
    }
    return null;
  }

  /**
   * Detect Nevada vs OOS supporting document for Edit modal (profile_tab_section first, then category key).
   */
  private resolveSupportingSectionForEditDoc(row: any): 'nevada' | 'oos' | null {
    const raw = row?._raw ?? row;
    const p = String(raw?.profile_tab_section ?? '').trim();
    if (isOosSupportingSection(p)) {
      return 'oos';
    }
    if (isNevadaSupportingSection(p)) {
      return 'nevada';
    }
    // If profile_tab_section is explicitly set to a known non-OOS/Nevada section, do not fall
    // through to category-key detection — key '10' collides with High School Diploma category.
    if (p) {
      return null;
    }
    const rawCat = String(raw?.cd_document_category ?? raw?.category ?? '').trim();
    const fromRaw = this.supportingSectionFromCategoryKey(rawCat);
    if (fromRaw) {
      return fromRaw;
    }
    const catKey = this.resolveDocKey(rawCat, this.docCategoryOptions);
    return this.supportingSectionFromCategoryKey(catKey);
  }

  private buildSupportingEditTypeOptions(
    section: 'nevada' | 'oos',
    currentTypeRaw: string
  ): { id: number; parentid: number | null; key: string; value: string }[] {
    const names = [...this.uniqueSupportingDocTestNames(section)];
    const cur = String(currentTypeRaw ?? '').trim();
    if (cur && !names.some(n => n === cur)) {
      names.push(cur);
      names.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    }
    return names.map(n => ({ id: 0, parentid: null, key: n, value: n }));
  }

  /**
   * Detect education/experience tab section from the document's profile_tab_section.
   */
  private resolveProfileTabSection(row: any): string | null {
    const raw = row?._raw ?? row;
    const p = String(raw?.profile_tab_section ?? '').trim();
    if (!p) return null;
    const eduSection = normalizeEducationProfileTabSection(p);
    if (eduSection) return eduSection;
    if (LookupUserActivitiesTabComponent.EXPERIENCE_TAB_SECTIONS.includes(p)) return p;
    // Map human-readable labels saved from communication upload to internal keys
    const expSection = PROFILE_DOC_EXPERIENCE_SECTION_LABELS[p];
    if (expSection) return expSection;
    return null;
  }

  /**
   * Build category options for an education/experience section document being edited.
   */
  private buildEducationExpCategoryOptions(section: string): { id: number; key: string; value: string }[] {
    switch (section) {
      case 'academic-Background': {
        const sectionCats = this.allSupportingDocCategories.filter(
          (c) => String(c.optionaldata?.section_name ?? '').trim() === 'Academic Background'
        );
        if (sectionCats.length) {
          return sectionCats.map((c, i) => ({
            id: i + 1,
            key: c.key,
            value: c.value
          }));
        }
        const match = this.docCategoryOptions.find(
          (c) => c.value === 'Academic Background' || c.key === 'Academic Background'
        );
        return match ? [match] : [{ id: 1, key: 'Academic Background', value: 'Academic Background' }];
      }
      case 'Additional_prof_dev': {
        const sectionCats = this.allSupportingDocCategories.filter(
          (c) => String(c.optionaldata?.section_name ?? '').trim() === 'Additional Professional Development'
        );
        if (sectionCats.length) {
          return sectionCats.map((c, i) => ({ id: i + 1, key: c.key, value: c.value }));
        }
        const match = this.docCategoryOptions.find(
          (c) => c.value === 'Professional Development Certificate' || c.value === 'Additional Professional Development'
        );
        return match ? [match] : [{ id: 1, key: 'Professional Development Certificate', value: 'Professional Development Certificate' }];
      }
      case 'employment_prof_dev': {
        const sectionCats = this.allSupportingDocCategories.filter(
          (c) => String(c.optionaldata?.section_name ?? '').trim() === 'District / Charter Employment'
        );
        if (sectionCats.length) {
          return sectionCats.map((c, i) => ({ id: i + 1, key: c.key, value: c.value }));
        }
        const match = this.docCategoryOptions.find(
          (c) => c.value === 'District / Charter Training Transcript' || c.value === 'District / Charter Employment'
        );
        return match ? [match] : [{ id: 1, key: 'District / Charter Training Transcript', value: 'District / Charter Training Transcript' }];
      }
      case 'out-of-state-lic_notes': {
        return [{ id: 1, key: 'Out of State or Country Licensure', value: 'Out of State or Country Licensure' }];
      }
      case 'verifiable-teaching-experience': {
        return [{ id: 1, key: 'Verifiable Teaching Experience', value: 'Verifiable Teaching Experience' }];
      }
      case 'business-industry-support': {
        return [{ id: 1, key: 'Verifiable Business and Industry Work Experience', value: 'Verifiable Business and Industry Work Experience' }];
      }
      case 'employment-support': {
        return [{ id: 1, key: 'Employment', value: 'Employment' }];
      }
      default:
        return this.docCategoryOptions;
    }
  }

  /**
   * Build type options for an education/experience section document being edited.
   */
  private buildEducationExpTypeOptions(
    section: string,
    currentType?: string,
    categoryKey?: string
  ): { id: number; parentid: number | null; key: string; value: string }[] {
    let names: string[] = [];
    switch (section) {
      case 'academic-Background': {
        const catKey = String(categoryKey ?? '').trim();
        const ctx = this.getProfileDocumentLabelContext();
        if (catKey && !isAcademicBackgroundInstitutionCategory(catKey, ctx)) {
          const cached = this.supportingDocTypesCache[catKey] ?? [];
          const cur = String(currentType ?? '').trim();
          const options = cached.map((t) => ({ id: 0, parentid: null, key: t.key, value: t.value }));
          if (cur && !options.some((o) => o.key === cur || o.value === cur)) {
            options.push({ id: 0, parentid: null, key: cur, value: cur });
          }
          return options;
        }
        const rows: any[] = (this.row as any)?.p_education ?? [];
        names = [...new Set(rows.map((r) => String(r?.institution_name ?? '').trim()).filter(Boolean))].sort();
        break;
      }
      case 'Additional_prof_dev': {
        // Provider names from educator's Additional Professional Development grid rows
        const rows: any[] = (this.row as any)?.p_prof_dev_additional ?? [];
        names = [...new Set(rows.map((r: any) => String(r?.provider_name ?? '').trim()).filter(Boolean))].sort();
        break;
      }
      case 'employment_prof_dev': {
        // District names from educator's District / Charter Employment grid rows
        const rows: any[] = (this.row as any)?.p_prof_dev_district_charter ?? [];
        names = [...new Set(rows.map((r: any) => String(r?.district_charter_name ?? '').trim()).filter(Boolean))].sort();
        break;
      }
      case 'out-of-state-lic_notes': {
        const rows: any[] = (this.row as any)?.p_profile_out_of_state_license ?? [];
        names = [...new Set(rows.map(r => String(r?.state ?? '').trim()).filter(Boolean))].sort();
        break;
      }
      case 'verifiable-teaching-experience': {
        const rows: any[] = (this.row as any)?.p_teaching_experience ?? [];
        names = [...new Set(rows.map(r => String(r?.state ?? '').trim()).filter(Boolean))].sort();
        break;
      }
      case 'business-industry-support': {
        const rows: any[] = (this.row as any)?.p_profile_business_industry_experience ?? [];
        names = [...new Set(rows.map(r => String(r?.employer_name ?? '').trim()).filter(Boolean))].sort();
        break;
      }
      case 'employment-support': {
        // District / Charter Training Transcript — types are district names from the district/charter grid
        const rows: any[] = (this.row as any)?.p_prof_dev_district_charter ?? [];
        names = [...new Set(rows.map(r => String(r?.district_charter_name ?? '').trim()).filter(Boolean))].sort();
        break;
      }
      default:
        return this.filteredDocTypeOptions;
    }
    const cur = String(currentType ?? '').trim();
    if (cur && !names.includes(cur)) {
      names.push(cur);
      names.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    }
    return names.map(n => ({ id: 0, parentid: null, key: n, value: n }));
  }

  /** Category selected in supporting-doc upload; types stay grid test names (not CMS document types). */
  onSupportingDocCategoryChange(_categoryKey: string): void {
    // No getTypesByCategory lookup — Nevada/OOS type options remain test names from the grid.
  }

  openSupportingDocUpload(section: 'nevada' | 'oos'): void {
    this.supportingDocUploadSection = section;
    this.staffDocNoticeModal.open({ contentVariant: 'default' }).then((confirmed) => {
      if (!confirmed) return;
      const s = this.supportingDocUploadSection;
      const testNames = this.uniqueSupportingDocTestNames(s);

      this.supportingDocUploadCategoryOptions =
        s === 'nevada' ? this.nvSupportingDocCategoryOptions : this.oosSupportingDocCategoryOptions;
      if (!this.supportingDocUploadCategoryOptions.length) {
        this.alertService.error(
          'Document category is not configured for this section. Please contact support or verify lookup data.'
        );
        return;
      }

      this.docUploadFile = null;
      this.docUploadError = null;
      this.docDropzoneActive = false;

      this.supportingDocTypeOptions = testNames.map(n => ({ key: n, value: n }));

      const lockedCategory = this.supportingDocUploadCategoryOptions[0];
      this.supportingDocUploadForm.get('category')?.enable({ emitEvent: false });
      this.supportingDocUploadForm.reset({
        category: lockedCategory.key,
        type: '',
        notes: ''
      });
      this.supportingDocUploadCategoryLabel = lockedCategory.value;
      this.supportingDocUploadCategoryDisabled = true;
      this.initialSupportingDocUploadCategoryKey = lockedCategory.key;
      this.supportingDocUploadForm.get('category')?.disable({ emitEvent: false });

      this.supportingDocUploadModalRef = this.ngbService.open(this.supportingDocUploadModalTpl, {
        size: 'lg',
        centered: true,
        backdrop: 'static',
        keyboard: false,
        windowClass: 'activities-doc-modal custom-activities-doc-modal'
      });
    });
  }

  closeSupportingDocUploadModal(): void {
    if (this.supportingDocUploadSaving) {
      return;
    }
    const notes = this.supportingDocUploadForm.get('notes')?.value?.trim();
    const typeVal = this.supportingDocUploadForm.get('type')?.value;
    const categoryVal = String(this.supportingDocUploadForm.getRawValue().category ?? '').trim();
    const initialCategory = String(this.initialSupportingDocUploadCategoryKey ?? '').trim();
    const categoryChanged = categoryVal !== initialCategory;
    const hasDraft = !!(
      this.docUploadFile ||
      notes ||
      (typeVal != null && String(typeVal).trim() !== '') ||
      categoryChanged
    );
    if (hasDraft) {
      this.openDiscardUploadDraftModal(() => this._actuallycloseSupportingDocUploadModal());
    } else {
      this._actuallycloseSupportingDocUploadModal();
    }
  }

  private _actuallycloseSupportingDocUploadModal(): void {
    this.supportingDocUploadModalRef?.close();
    this.supportingDocUploadModalRef = null;
    this.docUploadFile = null;
    this.docUploadError = null;
    this.supportingDocUploadCategoryOptions = [];
    this.supportingDocUploadCategoryDisabled = false;
    this.supportingDocUploadCategoryLabel = '';
    this.initialSupportingDocUploadCategoryKey = '';
    this.supportingDocUploadForm.get('category')?.enable({ emitEvent: false });
    this.supportingDocUploadForm.reset({ category: '', type: '', notes: '' });
  }

  async onSaveSupportingDocUpload(): Promise<void> {
    if (!this.docUploadFile) {
      this.docUploadError = 'Please select a file.';
      return;
    }
    if (this.supportingDocUploadForm.invalid) {
      this.supportingDocUploadForm.markAllAsTouched();
      return;
    }
    const uploadValues = this.supportingDocUploadForm.getRawValue();
    const catKey = uploadValues.category;
    const typeKey = uploadValues.type;
    const catOpt = this.supportingDocUploadCategoryOptions.find((c) => c.key === catKey);
    // Nevada/OOS type is a grid test name (key === display); do not resolve via CMS lookups.
    const typeDisplay = String(typeKey ?? '').trim();

    const proceed = await this.guardProfileDocumentDuplicateUpload({
      categoryKey: catKey,
      typeKey: typeDisplay,
      fileName: this.docUploadFile.name,
      screenName:
        this.supportingDocUploadSection === 'oos'
          ? 'Lookup User - Activities OOS Supporting'
          : 'Lookup User - Activities Nevada Supporting',
      categoryDisplay: catOpt?.value,
      typeDisplay
    });
    if (!proceed) {
      this.docUploadFile = null;
      this.docUploadError = null;
      return;
    }

    const profileTabSection = this.supportingDocUploadSection === 'oos'
      ? 'Other Testing'
      : 'Nevada Praxis Testing';

    const isSupportingChildSupport = this.isCaseDocCategory(catKey, catOpt?.value);

    const uploadData: Record<string, unknown> = {
      person_id: this.personId ? Number(this.personId) : null,
      uploaded_by: this.getCurrentUserId(),
      uploaded_at: new Date().toISOString(),
      file_name: this.docUploadFile.name,
      cd_document_category: catKey,
      cd_document_type: typeKey,
      notes: this.supportingDocUploadForm.value.notes || '',
      profile_tab_section: profileTabSection,
      formid: LOOKUP_USER_SECTION_FORM_IDS[profileTabSection] ?? null,
      ...(isSupportingChildSupport ? { document_status: 'Case' } : {})
    };

    this.supportingDocUploadSaving = true;
    educatorDocumentUploadWithClientBlobPut(this.httpService, this.docUploadFile, uploadData)
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          this.supportingDocUploadSaving = false;
        })
      )
      .subscribe({
        next: (res: any) => {
          if (res?.success === false) {
            this.alertService.error(res.message || 'Failed to upload document.');
            return;
          }
          this.alertService.success(this.toastMessage['T0005'] || 'Document uploaded successfully.');
          this.cacheProfileUploadAfterSuccess({
            cd_document_category: catKey,
            cd_document_type: typeDisplay,
            document_category: catOpt?.value,
            document_type: typeDisplay,
            file_name: this.docUploadFile!.name,
            profile_tab_section: profileTabSection,
            ...(isSupportingChildSupport ? { document_status: 'Case', status: 'Case' } : {})
          });
          this._actuallycloseSupportingDocUploadModal();
          this.requestDashboardRefresh.emit();
        },
        error: () => {
          this.alertService.error('Failed to upload document. Please try again.');
        }
      });
  }

  // ── Compliance: History Disclosure Supporting Documents upload ──

  openComplianceHistoryDisclosureUpload(): void {
    this.staffDocNoticeModal.open({ contentVariant: 'default' }).then((confirmed) => {
      if (!confirmed) return;
      this.docUploadFile = null;
      this.docUploadError = null;
      this.docDropzoneActive = false;
      this.complianceHdUploadForm.reset({ category: '', type: '' });
      this.complianceHdFilteredTypeOptions = [];
      this.complianceHdCategoryLookupMissing = false;
      this.complianceHdCategoryLoadError = null;
      this.supportingDocLookupService
        .getCategoriesBySectionName('History Disclosure')
        .pipe(take(1), takeUntil(this.destroy$))
        .subscribe((cats: SupportingDocumentGridOption[]) => {
          this.complianceHdCategoryRows = cats;
          if (cats.length === 0) {
            this.alertService.error(
              'No History Disclosure categories are available. Please verify lookup configuration.'
            );
            return;
          }
          this.complianceHdUploadModalRef = this.ngbService.open(this.complianceHdUploadModalTpl, {
            size: 'lg',
            centered: true,
            backdrop: 'static',
            keyboard: false,
            windowClass: 'activities-doc-modal custom-activities-doc-modal'
          });
        });
    });
  }

  onComplianceHistoryDisclosureCategoryChange(): void {
    const catKey = String(this.complianceHdUploadForm.get('category')?.value ?? '').trim();
    this.complianceHdUploadForm.patchValue({ type: '' });
    this.complianceHdFilteredTypeOptions = [];
    this.complianceHdCategoryLookupMissing = false;
    if (!catKey) return;
    const selected = this.complianceHdCategoryRows.find(c => c.key === catKey);
    if (!selected) {
      this.complianceHdCategoryLookupMissing = true;
      return;
    }
    this.supportingDocLookupService
      .getTypesByCategory(selected)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((types: SupportingDocumentGridOption[]) => {
        this.complianceHdFilteredTypeOptions = types;
        this.supportingDocTypesCache[catKey] = types;
      });
  }

  private hasComplianceHistoryDisclosureUploadDraft(): boolean {
    const v = this.complianceHdUploadForm?.getRawValue() ?? {};
    return !!(
      this.docUploadFile ||
      String(v.category || '').trim() ||
      String(v.type || '').trim()
    );
  }

  closeComplianceHistoryDisclosureUploadModal(): void {
    if (this.complianceSupportingUploadSaving) {
      return;
    }
    if (this.hasComplianceHistoryDisclosureUploadDraft()) {
      this.openDiscardUploadDraftModal(() => this._actuallyCloseComplianceHistoryDisclosureUploadModal());
    } else {
      this._actuallyCloseComplianceHistoryDisclosureUploadModal();
    }
  }

  private _actuallyCloseComplianceHistoryDisclosureUploadModal(): void {
    this.complianceHdUploadModalRef?.close();
    this.complianceHdUploadModalRef = null;
    this.docUploadFile = null;
    this.docUploadError = null;
    this.complianceHdUploadForm.reset({ category: '', type: '' });
    this.complianceHdFilteredTypeOptions = [];
    this.complianceHdCategoryRows = [];
    this.complianceHdCategoryLookupMissing = false;
    this.complianceHdCategoryLoadError = null;
  }

  async onSaveComplianceHistoryDisclosureUpload(): Promise<void> {
    if (!this.docUploadFile) {
      this.docUploadError = 'Please select a file.';
      return;
    }
    if (this.complianceHdUploadForm.invalid) {
      this.complianceHdUploadForm.markAllAsTouched();
      return;
    }
    const catKey = String(this.complianceHdUploadForm.value.category ?? '').trim();
    const typeKey = String(this.complianceHdUploadForm.value.type ?? '').trim();
    if (!catKey || !this.complianceHdCategoryRows.some(c => c.key === catKey || String(c.key) === catKey)) {
      this.complianceHdCategoryLookupMissing = true;
      this.alertService.error('Select a valid document category.');
      return;
    }
    if (!typeKey || !this.complianceHdFilteredTypeOptions.some(
      t => t.key === typeKey || String(t.key) === typeKey
    )) {
      this.alertService.error('Select a valid document type.');
      return;
    }

    const catOpt = this.complianceHdCategoryRows.find(
      (c) => c.key === catKey || String(c.key) === catKey
    );
    const typeOpt = this.complianceHdFilteredTypeOptions.find(
      (t) => t.key === typeKey || String(t.key) === typeKey
    );

    const proceed = await this.guardProfileDocumentDuplicateUpload({
      categoryKey: catKey,
      typeKey,
      fileName: this.docUploadFile.name,
      screenName: 'Lookup User - Activities Compliance History Disclosure',
      categoryDisplay: catOpt?.value,
      typeDisplay: typeOpt?.value
    });
    if (!proceed) {
      this.docUploadFile = null;
      this.docUploadError = null;
      return;
    }

    const complianceSection = LookupUserActivitiesTabComponent.COMPLIANCE_HISTORY_DISCLOSURE_PROFILE_SECTION;
    const uploadData: Record<string, unknown> = {
      person_id: this.personId ? Number(this.personId) : null,
      uploaded_by: this.getCurrentUserId(),
      uploaded_at: new Date().toISOString(),
      file_name: this.docUploadFile.name,
      cd_document_category: catKey,
      cd_document_type: typeKey,
      notes: '',
      profile_tab_section: complianceSection,
      formid: LOOKUP_USER_SECTION_FORM_IDS[complianceSection] ?? null,
      document_status: 'Case'
    };

    this.complianceSupportingUploadSaving = true;
    educatorDocumentUploadWithClientBlobPut(this.httpService, this.docUploadFile, uploadData)
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          this.complianceSupportingUploadSaving = false;
        })
      )
      .subscribe({
        next: (res: any) => {
          if (res?.success === false) {
            this.alertService.error(res.message || 'Failed to upload document.');
            return;
          }
          this.alertService.success(this.toastMessage['T0005'] || 'Document uploaded successfully.');
          this.cacheProfileUploadAfterSuccess({
            cd_document_category: catKey,
            cd_document_type: typeKey,
            document_category: catOpt?.value,
            document_type: typeOpt?.value,
            file_name: this.docUploadFile!.name,
            profile_tab_section: complianceSection,
            document_status: 'Case',
            status: 'Case'
          });
          this._actuallyCloseComplianceHistoryDisclosureUploadModal();
          this.requestDashboardRefresh.emit();
        },
        error: () => {
          this.alertService.error('Failed to upload document. Please try again.');
        }
      });
  }

  // ── Export & Column Chooser (Queues-style via app-grid-actions) ──

  exportNvSupportingDocs(): void {
    if (!this.nvSupportingDocsGrid) return;
    const cols = columnsForExcelExport(this.supportingDocsHeaders(), this.nvSupportingDocsVisibleColumns);
    if (cols.length === 0) {
      this.alertService.warn('No columns selected for export');
      return;
    }
    this.nvSupportingDocsGrid.exportFilteredDataToExcel(cols, 'NevadaSupportingDocuments.xlsx');
  }

  exportOosSupportingDocs(): void {
    if (!this.oosSupportingDocsGrid) return;
    const cols = columnsForExcelExport(this.supportingDocsHeaders(), this.oosSupportingDocsVisibleColumns);
    if (cols.length === 0) {
      this.alertService.warn('No columns selected for export');
      return;
    }
    this.oosSupportingDocsGrid.exportFilteredDataToExcel(cols, 'OOSSupportingDocuments.xlsx');
  }

  toggleNvSupportingDocsColumnChooser(): void {
    this.showNvSupportingDocsColumnChooser = !this.showNvSupportingDocsColumnChooser;
  }

  closeNvSupportingDocsColumnChooser(): void {
    this.showNvSupportingDocsColumnChooser = false;
  }

  onNvSupportingDocsColumnToggle(ev: { key: string; col: ColumnConfig }): void {
    if (ev.col.disabled) return;
    const cur = this.nvSupportingDocsVisibleColumns[ev.key] !== false;
    this.nvSupportingDocsVisibleColumns = { ...this.nvSupportingDocsVisibleColumns, [ev.key]: !cur };
    this.nevadaSupportingDocsGridConfig = this.nevadaSupportingDocsGridConfig.updateConfig({
      visibleColumns: { ...this.nvSupportingDocsVisibleColumns },
      _changeKey: Date.now()
    });
  }

  toggleOosSupportingDocsColumnChooser(): void {
    this.showOosSupportingDocsColumnChooser = !this.showOosSupportingDocsColumnChooser;
  }

  closeOosSupportingDocsColumnChooser(): void {
    this.showOosSupportingDocsColumnChooser = false;
  }

  onOosSupportingDocsColumnToggle(ev: { key: string; col: ColumnConfig }): void {
    if (ev.col.disabled) return;
    const cur = this.oosSupportingDocsVisibleColumns[ev.key] !== false;
    this.oosSupportingDocsVisibleColumns = { ...this.oosSupportingDocsVisibleColumns, [ev.key]: !cur };
    this.oosSupportingDocsGridConfig = this.oosSupportingDocsGridConfig.updateConfig({
      visibleColumns: { ...this.oosSupportingDocsVisibleColumns },
      _changeKey: Date.now()
    });
  }

  exportComplianceSupportingDocs(): void {
    if (!this.complianceSupportingDocsGrid) return;
    const cols = columnsForExcelExport(this.supportingDocsHeaders(), this.complianceSupportingDocsVisibleColumns);
    if (cols.length === 0) {
      this.alertService.warn('No columns selected for export');
      return;
    }
    this.complianceSupportingDocsGrid.exportFilteredDataToExcel(cols, 'ComplianceHistoryDisclosureSupportingDocuments.xlsx');
  }

  toggleComplianceSupportingDocsColumnChooser(): void {
    this.showComplianceSupportingDocsColumnChooser = !this.showComplianceSupportingDocsColumnChooser;
  }

  closeComplianceSupportingDocsColumnChooser(): void {
    this.showComplianceSupportingDocsColumnChooser = false;
  }

  onComplianceSupportingDocsColumnToggle(ev: { key: string; col: ColumnConfig }): void {
    if (ev.col.disabled) return;
    const cur = this.complianceSupportingDocsVisibleColumns[ev.key] !== false;
    this.complianceSupportingDocsVisibleColumns = { ...this.complianceSupportingDocsVisibleColumns, [ev.key]: !cur };
    this.complianceSupportingDocsGridConfig = this.complianceSupportingDocsGridConfig.updateConfig({
      visibleColumns: { ...this.complianceSupportingDocsVisibleColumns },
      _changeKey: Date.now()
    });
  }

  exportAppHistory(): void {
    if (!this.appHistoryGrid) return;
    const cols = columnsForExcelExport(this.applicationHistoryGridConfig.customHeaders, this.appHistoryVisibleColumns);
    if (cols.length === 0) {
      this.alertService.warn('No columns selected for export');
      return;
    }
    this.appHistoryGrid.exportFilteredDataToExcel(cols, 'ApplicationHistory.xlsx');
  }

  toggleAppHistoryColumnChooser(): void {
    this.showAppHistoryColumnChooser = !this.showAppHistoryColumnChooser;
  }

  closeAppHistoryColumnChooser(): void {
    this.showAppHistoryColumnChooser = false;
  }

  onAppHistoryColumnToggle(ev: { key: string; col: ColumnConfig }): void {
    if (ev.col.disabled) return;
    const cur = this.appHistoryVisibleColumns[ev.key] !== false;
    this.appHistoryVisibleColumns = { ...this.appHistoryVisibleColumns, [ev.key]: !cur };
    this.applicationHistoryGridConfig = this.applicationHistoryGridConfig.updateConfig({
      visibleColumns: { ...this.appHistoryVisibleColumns },
      _changeKey: Date.now()
    });
  }

  exportNevadaTesting(): void {
    if (!this.nevadaTestGrid) return;
    const cols = columnsForExcelExport(this.nevadaTestingGridConfig.customHeaders, this.nevadaTestingVisibleColumns);
    if (cols.length === 0) {
      this.alertService.warn('No columns selected for export');
      return;
    }
    this.nevadaTestGrid.exportFilteredDataToExcel(cols, 'NevadaTesting.xlsx');
  }

  toggleNevadaTestingColumnChooser(): void {
    this.showNevadaTestingColumnChooser = !this.showNevadaTestingColumnChooser;
  }

  closeNevadaTestingColumnChooser(): void {
    this.showNevadaTestingColumnChooser = false;
  }

  onNevadaTestingColumnToggle(ev: { key: string; col: ColumnConfig }): void {
    if (ev.col.disabled) return;
    const cur = this.nevadaTestingVisibleColumns[ev.key] !== false;
    this.nevadaTestingVisibleColumns = { ...this.nevadaTestingVisibleColumns, [ev.key]: !cur };
    this.nevadaTestingGridConfig = this.nevadaTestingGridConfig.updateConfig({
      visibleColumns: { ...this.nevadaTestingVisibleColumns },
      _changeKey: Date.now()
    });
  }

  exportOosTesting(): void {
    if (!this.oosTestGrid) return;
    const cols = columnsForExcelExport(this.oosTestingGridConfig.customHeaders, this.oosTestingVisibleColumns);
    if (cols.length === 0) {
      this.alertService.warn('No columns selected for export');
      return;
    }
    this.oosTestGrid.exportFilteredDataToExcel(cols, 'OutOfStateTesting.xlsx');
  }

  toggleOosTestingColumnChooser(): void {
    this.showOosTestingColumnChooser = !this.showOosTestingColumnChooser;
  }

  closeOosTestingColumnChooser(): void {
    this.showOosTestingColumnChooser = false;
  }

  onOosTestingColumnToggle(ev: { key: string; col: ColumnConfig }): void {
    if (ev.col.disabled) return;
    const cur = this.oosTestingVisibleColumns[ev.key] !== false;
    this.oosTestingVisibleColumns = { ...this.oosTestingVisibleColumns, [ev.key]: !cur };
    this.oosTestingGridConfig = this.oosTestingGridConfig.updateConfig({
      visibleColumns: { ...this.oosTestingVisibleColumns },
      _changeKey: Date.now()
    });
  }

  exportBackgroundCheck(): void {
    if (!this.backgroundCheckGrid) return;
    const cols = columnsForExcelExport(this.backgroundCheckGridConfig.customHeaders, this.backgroundCheckVisibleColumns);
    if (cols.length === 0) {
      this.alertService.warn('No columns selected for export');
      return;
    }
    this.backgroundCheckGrid.exportFilteredDataToExcel(cols, 'BackgroundCheck.xlsx');
  }

  toggleBackgroundCheckColumnChooser(): void {
    this.showBackgroundCheckColumnChooser = !this.showBackgroundCheckColumnChooser;
  }

  closeBackgroundCheckColumnChooser(): void {
    this.showBackgroundCheckColumnChooser = false;
  }

  onBackgroundCheckColumnToggle(ev: { key: string; col: ColumnConfig }): void {
    if (ev.col.disabled) return;
    const cur = this.backgroundCheckVisibleColumns[ev.key] !== false;
    this.backgroundCheckVisibleColumns = { ...this.backgroundCheckVisibleColumns, [ev.key]: !cur };
    this.backgroundCheckGridConfig = this.backgroundCheckGridConfig.updateConfig({
      visibleColumns: { ...this.backgroundCheckVisibleColumns },
      _changeKey: Date.now()
    });
  }

  exportProfileDocuments(): void {
    if (!this.profileDocumentsGrid) return;
    const cols = columnsForExcelExport(this.profileDocumentsGridConfig.customHeaders, this.profileDocumentsVisibleColumns);
    if (cols.length === 0) {
      this.alertService.warn('No columns selected for export');
      return;
    }
    this.profileDocumentsGrid.exportFilteredDataToExcel(cols, 'ProfileDocuments.xlsx');
  }

  toggleProfileDocumentsColumnChooser(): void {
    this.showProfileDocumentsColumnChooser = !this.showProfileDocumentsColumnChooser;
  }

  closeProfileDocumentsColumnChooser(): void {
    this.showProfileDocumentsColumnChooser = false;
  }

  onProfileDocumentsColumnToggle(ev: { key: string; col: ColumnConfig }): void {
    if (ev.col.disabled) return;
    const cur = this.profileDocumentsVisibleColumns[ev.key] !== false;
    this.profileDocumentsVisibleColumns = { ...this.profileDocumentsVisibleColumns, [ev.key]: !cur };
    this.profileDocumentsGridConfig = this.profileDocumentsGridConfig.updateConfig({
      visibleColumns: { ...this.profileDocumentsVisibleColumns },
      _changeKey: Date.now()
    });
  }

  // ── Application History: Row Download PDF ──

  downloadApplicationRow(data: any): void {
    const appId = data?.id || data?.application_id;
    if (!appId) {
      this.alertService.warn('No application ID found for this row.');
      return;
    }
    this.commonService.PDFGenerate(appId);
  }

  // ── Application History: Resend Submission Email ──

  sendApplicationEmail(data: any): void {
    const appId = data?.id || data?.application_id;
    if (!appId) {
      this.alertService.warn('No application ID found for this row.');
      return;
    }
    const options: NgbModalOptions = { backdrop: 'static', centered: true, size: 'md' };
    const modalRef = this.ngbService.open(ModalComponent, options);
    const config: ModalConfig = {
      buttons: [
        { text: 'Cancel', code: 'N', btnOutline: true },
        { text: 'Proceed', code: 'Y', btnOutline: false }
      ],
      heading: 'Resend Application Submission Email',
      modalContent: `Do you want to resend the application submission email? This will send the email again to the recipient`,
      hasTemplate: true
    };
    modalRef.componentInstance.modalConfig = config;
    modalRef.componentInstance.emitData.pipe(take(1)).subscribe((response: any) => {
      modalRef.close();
      if (response?.code === 'Y' || response?.confirmed === true) {
        this.httpService.post(API_ENDPOINTS.EDUCATOR_RESEND_SUBMISSION_EMAIL, {
          applicationId: appId,
          personId: this.personId
        }).pipe(take(1), takeUntil(this.destroy$)).subscribe({
          next: (res: any) => {
            if (res?.status === 'success') {
              this.alertService.success('Submission email sent successfully.');
            } else {
              this.alertService.error(res?.message || 'Failed to send email.');
            }
          },
          error: () => {
            this.alertService.error('Failed to send submission email. Please try again.');
          }
        });
      }
    });
  }

  // ── Edit Nevada Test ──

  onEditNevadaTest(data: any): void {
    this.editNevadaTestRow = data;
    const raw = data?._raw || data;
    this.editNevadaTestForm.patchValue({
      testName: this.resolveNevadaTestNameKeyForForm(raw),
      testDate: this.testDateApiToDisplay(raw.test_date),
      score: raw.score ?? null
    });
    this.editNevadaTestFormSnapshot = this.editNevadaTestForm.getRawValue();
    const opts: NgbModalOptions = { backdrop: 'static', centered: true, size: 'xl', windowClass: 'license-provision-modal custom-license-provision-modal' };

    this.editNevadaTestModalRef = this.ngbService.open(this.editNevadaTestModal, opts);
    this.editNevadaTestModalRef.result.then(
      () => this.clearEditNevadaTestModalState(),
      () => this.clearEditNevadaTestModalState()
    );
  }

  private clearEditNevadaTestModalState(): void {
    this.editNevadaTestModalRef = null;
    this.editNevadaTestRow = null;
    this.editNevadaTestFormSnapshot = null;
  }

  onResetEditNevadaTestForm(): void {
    const snap = this.editNevadaTestFormSnapshot;
    if (!snap) {
      return;
    }
    this.editNevadaTestForm.patchValue(snap as { testName: string; testDate: string; score: number | null });
    this.editNevadaTestForm.markAsPristine();
    this.editNevadaTestForm.markAsUntouched();
  }

  onSaveNevadaTestEdit(): void {
    if (this.editNevadaTestForm.invalid) {
      this.editNevadaTestForm.markAllAsTouched();
      this.alertService.error(this.toastMessage['T0017'] || 'Please fill in all required fields.');
      return;
    }
    const raw = this.editNevadaTestRow?._raw || this.editNevadaTestRow;
    const id = this.resolveNevadaTestNumericId(raw);
    if (id == null) { this.alertService.error('Missing test ID.'); return; }
    const val = this.editNevadaTestForm.value;
    const nvTestNameDisplay = this.testNameOptions.find(o => o.key === val.testName)?.value ?? val.testName;
    const nvList: any[] = Array.isArray(this.row?.p_nevada_testing) ? this.row!.p_nevada_testing! : [];
    console.log('[NV Edit Dup] form:', { testName: val.testName, nvTestNameDisplay, testDate: val.testDate, score: val.score });
    const isNvDuplicate = nvList.some((t: any) => {
      const existingId = this.resolveNevadaTestNumericId(t);
      const rowDate = this.formatGridDate(t.test_date);
      const match = existingId !== id &&
        (this.normalizeStr(t.test_name) === this.normalizeStr(val.testName) ||
         this.normalizeStr(t.test_name) === this.normalizeStr(nvTestNameDisplay)) &&
        this.normalizeStr(rowDate) === this.normalizeStr(val.testDate) &&
        String(t.score) === String(val.score);
      console.log('[NV Edit Dup] row:', { test_name: t.test_name, rowDate, score: t.score, existingId }, '→ match:', match);
      return match;
    });
    if (isNvDuplicate) {
      this.alertService.error('Duplicate entry detected. This record already exists.');
      return;
    }
    this.httpService.post(API_ENDPOINTS.EDUCATOR_EDIT_NEVADA_TEST, {
      id,
      cdTestName: val.testName,
      testDate: this.testDateDisplayToIso(val.testDate),
      score: Number(val.score),
      result: this.resolveNevadaTestResult(val.testName, Number(val.score)),
      updatedBy: this.getCurrentUserId()
    }).pipe(take(1), takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        if (res?.status === 'success') {
          this.alertService.success(this.toastMessage['T0020'] || 'Nevada test updated successfully.');
          this.editNevadaTestModalRef?.close();
          this.requestDashboardRefresh.emit();
        } else {
          this.alertService.error(res?.message || 'Failed to update test.');
        }
      },
      error: () => this.alertService.error('Failed to update Nevada test.')
    });
  }

  // ── Delete Nevada Test ──

  onDeleteNevadaTest(data: any): void {
    const raw = data?._raw || data;
    const id = this.resolveNevadaTestNumericId(raw);
    if (id == null) { this.alertService.warn('No test ID found.'); return; }
    const options: NgbModalOptions = { backdrop: 'static', size: 'md', centered: true, windowClass: 'custom-delete-record-modal' };
    const modalRef = this.ngbService.open(ModalComponent, options);
    const config: ModalConfig = {
      buttons: [
        { text: 'Cancel', code: 'N', btnOutline: true },
        { text: 'Delete', code: 'Y', btnOutline: false }
      ],
      heading: 'Delete Record',
      headingAlign: 'left',
      modalContent: 'Are you sure you want to delete this record? This action cannot be undone.',
      modalContentClass: 'actdelcontent',
      hasTemplate: true,
      isDangerButton: true,
      panelClass: 'lookup-act-delete-modal'
    };
    modalRef.componentInstance.modalConfig = config;
    modalRef.componentInstance.emitData.pipe(take(1)).subscribe((response: any) => {
      modalRef.close();
      if (response?.code === 'Y' || response?.confirmed === true) {
        this.httpService.post(API_ENDPOINTS.EDUCATOR_DELETE_NEVADA_TEST, {
          id,
          updatedBy: Number(this.sessionStorage.getItem('userId') || 1)
        }).pipe(take(1), takeUntil(this.destroy$)).subscribe({
          next: (res: any) => {
            if (res?.status === 'success') {
              this.alertService.success(this.toastMessage['T0040'] || 'Nevada test deleted successfully.');
              this.requestDashboardRefresh.emit();
            } else {
              this.alertService.error(res?.message || 'Failed to delete test.');
            }
          },
          error: () => this.alertService.error('Failed to delete Nevada test.')
        });
      }
    });
  }

  // ── Edit OOS Test ──

  onEditOosTest(data: any): void {
    this.editOosTestRow = data;
    const raw = data?._raw || data;
    this.editOosTestForm.patchValue({
      state: this.resolveOosStateKeyForForm(raw),
      testName: raw.test_name ?? '',
      testDate: this.testDateApiToDisplay(raw.test_date),
      score: raw.score ?? null,
      result: raw.cd_result ?? raw.result ?? ''
    });
    this.editOosTestFormSnapshot = this.editOosTestForm.getRawValue();
    const opts: NgbModalOptions = { backdrop: 'static', centered: true, size: 'xl', windowClass: 'license-provision-modal custom-license-provision-modal' };
    this.editOosTestModalRef = this.ngbService.open(this.editOosTestModal, opts);
    this.editOosTestModalRef.result.then(
      () => this.clearEditOosTestModalState(),
      () => this.clearEditOosTestModalState()
    );
  }

  private clearEditOosTestModalState(): void {
    this.editOosTestModalRef = null;
    this.editOosTestRow = null;
    this.editOosTestFormSnapshot = null;
  }

  onResetEditOosTestForm(): void {
    const snap = this.editOosTestFormSnapshot;
    if (!snap) {
      return;
    }
    this.editOosTestForm.patchValue(
      snap as { state: string; testName: string; testDate: string; score: number | null; result: string }
    );
    this.editOosTestForm.markAsPristine();
    this.editOosTestForm.markAsUntouched();
  }

  onSaveOosTestEdit(): void {
    if (this.editOosTestForm.invalid) {
      this.editOosTestForm.markAllAsTouched();
      this.alertService.error(this.toastMessage['T0017'] || 'Please fill in all required fields.');
      return;
    }
    const raw = this.editOosTestRow?._raw || this.editOosTestRow;
    const id = this.resolveOosTestNumericId(raw);
    if (id == null) { this.alertService.error('Missing test ID.'); return; }
    const val = this.editOosTestForm.value;
    const oosStateDisplay = this.oosTestStateOptions.find(o => o.key === val.state)?.value ?? val.state;
    const oosResultDisplay = this.oosResultOptions.find(o => o.key === val.result)?.value ?? val.result;
    const oosList: any[] = Array.isArray(this.row?.p_out_of_state_testing) ? this.row!.p_out_of_state_testing! : [];
    console.log('[OOS Edit Dup] form:', { state: val.state, oosStateDisplay, testName: val.testName, testDate: val.testDate, score: val.score, result: val.result, oosResultDisplay });
    const isOosDuplicate = oosList.some((t: any) => {
      const existingId = this.resolveOosTestNumericId(t);
      const rowDate = this.formatGridDate(t.test_date);
      const rawResult = t.cd_result ?? t.result ?? '';
      const match = existingId !== id &&
        (this.normalizeStr(t.state) === this.normalizeStr(val.state) ||
         this.normalizeStr(t.state) === this.normalizeStr(oosStateDisplay)) &&
        this.normalizeStr(t.test_name) === this.normalizeStr(val.testName) &&
        this.normalizeStr(rowDate) === this.normalizeStr(val.testDate) &&
        String(t.score) === String(val.score) &&
        (this.normalizeStr(rawResult) === this.normalizeStr(val.result) ||
         this.normalizeStr(rawResult) === this.normalizeStr(oosResultDisplay));
      console.log('[OOS Edit Dup] row:', { state: t.state, test_name: t.test_name, rowDate, score: t.score, cd_result: t.cd_result, result: t.result, existingId }, '→ match:', match);
      return match;
    });
    if (isOosDuplicate) {
      this.alertService.error('Duplicate entry detected. This record already exists.');
      return;
    }
    this.httpService.post(API_ENDPOINTS.EDUCATOR_EDIT_OOS_TEST, {
      id,
      cdState: val.state,
      testName: val.testName,
      testDate: this.testDateDisplayToIso(val.testDate),
      score: Number(val.score),
      cdResult: val.result,
      updatedBy: this.getCurrentUserId()
    }).pipe(take(1), takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        if (res?.status === 'success') {
          this.alertService.success(this.toastMessage['T0020'] || 'Out-of-State test updated successfully.');
          this.editOosTestModalRef?.close();
          this.requestDashboardRefresh.emit();
        } else {
          this.alertService.error(res?.message || 'Failed to update test.');
        }
      },
      error: () => this.alertService.error('Failed to update Out-of-State test.')
    });
  }

  // ── Delete OOS Test ──

  onDeleteOosTest(data: any): void {
    const raw = data?._raw || data;
    const id = this.resolveOosTestNumericId(raw);
    if (id == null) { this.alertService.warn('No test ID found.'); return; }
    const options: NgbModalOptions = { backdrop: 'static', size: 'md', centered: true, windowClass: 'custom-delete-record-modal' };
    const modalRef = this.ngbService.open(ModalComponent, options);
    const config: ModalConfig = {
      buttons: [
        { text: 'Cancel', code: 'N', btnOutline: true },
        { text: 'Delete', code: 'Y', btnOutline: false }
      ],
      heading: 'Delete Record',
      headingAlign: 'left',
      modalContent: 'Are you sure you want to delete this record? This action cannot be undone.',
      modalContentClass: 'actdelcontent',
      hasTemplate: true,
      isDangerButton: true,
      panelClass: 'lookup-act-delete-modal'
    };
    modalRef.componentInstance.modalConfig = config;
    modalRef.componentInstance.emitData.pipe(take(1)).subscribe((response: any) => {
      modalRef.close();
      if (response?.code === 'Y' || response?.confirmed === true) {
        this.httpService.post(API_ENDPOINTS.EDUCATOR_DELETE_OOS_TEST, {
          id,
          updatedBy: Number(this.sessionStorage.getItem('userId') || 1)
        }).pipe(take(1), takeUntil(this.destroy$)).subscribe({
          next: (res: any) => {
            if (res?.status === 'success') {
              this.alertService.success(this.toastMessage['T0040'] || 'Out-of-State test deleted successfully.');
              this.requestDashboardRefresh.emit();
            } else {
              this.alertService.error(res?.message || 'Failed to delete test.');
            }
          },
          error: () => this.alertService.error('Failed to delete Out-of-State test.')
        });
      }
    });
  }

  confirmDeleteDocument(row: any): void {
    if (!this.canShowDeleteProfileDocument(row)) {
      this.alertService.warn('You are not permitted to delete this document.');
      return;
    }
    const options: NgbModalOptions = { backdrop: 'static', centered: true, size: 'md', windowClass: 'custom-delete-record-modal' };
    const modalRef = this.ngbService.open(ModalComponent, options);
    const config: ModalConfig = {
      buttons: [
        { text: 'Cancel', code: 'N', btnOutline: true },
        { text: 'Delete', code: 'Y', btnOutline: false }
      ],
      heading: 'Delete Document',
      headingAlign: 'left',
      modalContent: 'Are you sure you want to delete this document? This action cannot be undone.',
      modalContentClass: 'actdelcontent',
      hasTemplate: true,
      isDangerButton: true,
      panelClass: 'lookup-act-delete-modal'
    };
    modalRef.componentInstance.modalConfig = config;
    modalRef.componentInstance.emitData.pipe(take(1)).subscribe((response: any) => {
      modalRef.close();
      if (response?.code === 'Y' || response?.confirmed === true) {
        this.httpService.post(API_ENDPOINTS.EDUCATOR_DELETE_DOCUMENT, { document_id: row.document_id })
          .pipe(take(1), takeUntil(this.destroy$))
          .subscribe({
            next: (res: any) => {
              if (res?.success === false) {
                this.alertService.error(res.message || 'Failed to delete document.');
                return;
              }
              this.alertService.success(this.toastMessage['T0040'] || 'Document deleted successfully.');
              this.requestDashboardRefresh.emit();
            },
            error: () => {
              this.alertService.error('Failed to delete document. Please try again.');
            }
          });
      }
    });
  }

  confirmArchiveDocument(row: any): void {
    const options: NgbModalOptions = { backdrop: 'static', centered: true, size: 'md' };
    const modalRef = this.ngbService.open(ModalComponent, options);
    const config: ModalConfig = {
      buttons: [
        { text: 'Cancel', code: 'N', btnOutline: true },
        { text: 'Confirm', code: 'Y', btnOutline: false }
      ],
      heading: 'Archive Document',
      headingAlign: 'left',
      modalContent: 'Are you sure you want to archive the document?',
      hasTemplate: true
    };
    modalRef.componentInstance.modalConfig = config;
    modalRef.componentInstance.emitData.pipe(take(1)).subscribe((response: any) => {
      modalRef.close();
      if (response?.code === 'Y' || response?.confirmed === true) {
        this.httpService.post(API_ENDPOINTS.EDUCATOR_ARCHIVE_DOCUMENT, { document_id: row.document_id })
          .pipe(take(1), takeUntil(this.destroy$))
          .subscribe({
            next: (res: any) => {
              if (res?.success === false) {
                this.alertService.error(res.message || 'Failed to archive document.');
                return;
              }
              this.alertService.success(this.toastMessage['T0072'] || 'Document archived successfully.');
              this.requestDashboardRefresh.emit();
            },
            error: () => {
              this.alertService.error('Failed to archive document. Please try again.');
            }
          });
      }
    });
  }

  confirmUnarchiveDocument(row: any): void {
    const options: NgbModalOptions = { backdrop: 'static', centered: true, size: 'md' };
    const modalRef = this.ngbService.open(ModalComponent, options);
    const config: ModalConfig = {
      buttons: [
        { text: 'Cancel', code: 'N', btnOutline: true },
        { text: 'Confirm', code: 'Y', btnOutline: false }
      ],
      heading: 'Unarchive Document',
      headingAlign: 'left',
      modalContent: 'Are you sure you want to unarchive the document?',
      hasTemplate: true
    };
    modalRef.componentInstance.modalConfig = config;
    modalRef.componentInstance.emitData.pipe(take(1)).subscribe((response: any) => {
      modalRef.close();
      if (response?.code === 'Y' || response?.confirmed === true) {
        this.httpService.post(API_ENDPOINTS.EDUCATOR_UNARCHIVE_DOCUMENT, { document_id: row.document_id })
          .pipe(take(1), takeUntil(this.destroy$))
          .subscribe({
            next: (res: any) => {
              if (res?.success === false) {
                this.alertService.error(res.message || 'Failed to unarchive document.');
                return;
              }
              this.alertService.success(this.toastMessage['T0073'] || 'Document unarchived successfully.');
              this.requestDashboardRefresh.emit();
            },
            error: () => {
              this.alertService.error('Failed to unarchive document. Please try again.');
            }
          });
      }
    });
  }

  private resolveDocKey(raw: string, lookups: { key: string; value: string }[]): string {
    if (!raw) return '';
    const byKey = lookups.find(l => l.key === raw);
    if (byKey) return byKey.key;
    const byValue = lookups.find(l => l.value === raw);
    return byValue ? byValue.key : raw;
  }

  /** Soft-deleted or flagged inactive in DB — hidden from both Active and Archived tabs. */
  private isDocumentRemovedFromGrid(d: any): boolean {
    const ia = d?.is_active;
    if (ia === false || ia === 0 || String(ia).toLowerCase() === 'false') return true;
    const st = String(d?.status ?? d?.document_status ?? '').toLowerCase().trim();
    return st === 'deleted';
  }

  /** True when the row belongs on the Archived tab (not Active); excludes removed rows first via isDocumentRemovedFromGrid. */
  private isDocArchived(d: any): boolean {
    const st = String(d?.status ?? d?.document_status ?? '').toLowerCase().trim();
    return st === 'archived' || st === 'inactive';
  }

  private rebuildProfileDocumentsGrid(): void {
    const apps = Array.isArray(this.row?.p_application) ? this.row!.p_application! : [];
    const useArchived = this.documentsTab === 'archived';

    let visible = this.profileUploadDocuments().filter(d => !this.isDocumentRemovedFromGrid(d));
    visible = visible.map(doc => ({
      ...doc,
      status:
        doc.profile_tab_section === 'communication_notes'
          ? 'Verified'
          : doc.status
    }));
    const filtered = visible.filter(d => this.isDocArchived(d) === useArchived);

    const resolveDocInternalApplicationId = (d: any): string => {
      if (d.application_id != null && String(d.application_id).trim() !== '') {
        return String(d.application_id).trim();
      }
      const appNum =
        d.application_number != null && String(d.application_number).trim() !== ''
          ? String(d.application_number).trim()
          : '';
      if (!appNum) return '';
      const byNum = apps.find(
        (a: any) =>
          String(a.app_id ?? '').trim() === appNum ||
          String(a.application_number ?? '').trim() === appNum
      );
      return byNum?.application_id != null ? String(byNum.application_id).trim() : '';
    };

    const labelCtx = this.getProfileDocumentLabelContext();
    const rows = filtered.map((d: any, i: number) => {
      const docIdRaw = d.document_id ?? d.profile_document_upload_id ?? d.documentId;
      const document_id =
        docIdRaw != null && docIdRaw !== undefined && String(docIdRaw).trim() !== ''
          ? Number(docIdRaw)
          : NaN;
      const internalId = resolveDocInternalApplicationId(d);
      const matchedApp = apps.find((a: any) => String(a.application_id) === internalId);
      const displayId =
        d.application_number != null && String(d.application_number).trim() !== ''
          ? String(d.application_number).trim()
          : matchedApp?.app_id != null && String(matchedApp.app_id).trim() !== ''
            ? String(matchedApp.app_id).trim()
            : internalId;

      const docRecord = { ...d, _raw: d } as Record<string, unknown>;
      const category = resolveProfileDocumentCategoryLabel(docRecord, labelCtx);
      const docType = resolveProfileDocumentTypeLabel(docRecord, labelCtx);
      const uploadedBy = d.uploaded_by ?? d.uploadedBy ?? '';
      const uploadedAtRaw = d.uploaded_at ?? d.uploadedAt;
      const uploadedDisplayRaw = d.uploaded_date ?? d.uploadedDate;
      const uploadedDateForGrid = uploadedDisplayRaw ?? uploadedAtRaw;
      const notesRaw = d.document_notes ?? d.notes ?? '';
      let notes = '';
      if (notesRaw && typeof notesRaw === 'string') {
        try { notes = JSON.parse(notesRaw)?.notes ?? notesRaw; } catch { notes = notesRaw; }
      } else if (notesRaw && typeof notesRaw === 'object') {
        notes = (notesRaw as any).notes ?? '';
      }
      const uploadMs =
        uploadTimeMsFromEducatorDocumentRow({
          uploaded_at: uploadedAtRaw,
          uploaded_date: uploadedDisplayRaw,
          _raw: {
            ...d,
            uploaded_at: uploadedAtRaw,
            uploaded_date: uploadedDisplayRaw
          }
        }) ?? 0;
      return {
        // app-id column uses cell.data.id for navigation; must be internal application_id when linked
        id: internalId || `pd-${i}`,
        application_id: internalId ? displayId || '---' : '---',
        category_label: category,
        document_type: docType,
        file_name: d.file_name ?? '',
        uploaded_by: uploadedBy,
        uploaded_date: this.formatGridDate(uploadedDateForGrid),
        notes,
        document_status: d.status ?? d.document_status ?? '',
        document_id: Number.isFinite(document_id) ? document_id : d.document_id ?? d.profile_document_upload_id ?? null,
        _uploadMs: uploadMs > 0 ? uploadMs : null,
        _uploadSortMs: uploadMs,
        _raw: {
          ...d,
          uploaded_at: uploadedAtRaw,
          uploaded_date: uploadedDisplayRaw,
          profile_tab_section: (d.profile_tab_section && String(d.profile_tab_section).trim()) || ''
        }
      };
    });

    const docIdSort = (row: any): number => {
      const n = Number(row?.document_id);
      return Number.isFinite(n) ? n : 0;
    };
    rows.sort((a: any, b: any) => {
      const t = b._uploadSortMs - a._uploadSortMs;
      if (t !== 0) return t;
      return docIdSort(b) - docIdSort(a);
    });
    rows.forEach((r: any) => delete r._uploadSortMs);

    const headers = this.activitiesMetadataHeaders['profileDocuments'] || [];
    this.profileDocumentsAllColumns = withColumnChooserLock(headersToColumnConfigs(headers));
    this.profileDocumentsVisibleColumns = mergeVisibleColumnDefaultsWithChooserLock(
      this.profileDocumentsAllColumns.map(c => c.key),
      this.profileDocumentsVisibleColumns
    );
    this.profileDocumentsGridConfig = DataGridVO.create({
      customHeaders: headers,
      data: rows,
      showActionsColumn: true,
      showFiltering: true,
      showSearchRow: true,
      showPaging: true,
      customPageSize: 10,
      customNoDataText: this.activityGridNoRecordsText,
      role: 'registration',
      activeTab: 'default',
      showQueueActions: false,
      disableApplicationClick: false,
      visibleColumns: { ...this.profileDocumentsVisibleColumns },
      _changeKey: Date.now()
    });
  }

getSelectLabel(
  key: any,
  options: { key: any; value: string }[]
): string {
  if (!key || !options?.length) return '';

  const found = options.find(o => String(o.key) === String(key));
  if (!found?.value) return '';

  const trimmedLabel = found.value.trim();
  const wordCount = trimmedLabel.split(/\s+/).length;
  const charCount = trimmedLabel.length;

  return charCount > 38 ? found.value : '';
}

getCategoryLabel(
  key: any,
  options: { key: any; value: string }[]
): string {
  if (!key || !options?.length) return '';

  const found = options.find(o => String(o.key) === String(key));
  if (!found?.value) return '';

  const trimmedLabel = found.value.trim();
  const wordCount = trimmedLabel.split(/\s+/).length;
  const charCount = trimmedLabel.length;

  return charCount > 24 ? found.value : '';
}

getStateLabel(
  key: any,
  options: { key: any; value: string }[]
): string {
  if (!key || !options?.length) return '';

  const found = options.find(o => String(o.key) === String(key));
  if (!found?.value) return '';

  const trimmedLabel = found.value.trim();
  const wordCount = trimmedLabel.split(/\s+/).length;
  const charCount = trimmedLabel.length;

  return charCount > 21 ? found.value : '';
}

}
